// Invoice backup workbook — the labor and equipment invoice for the weeks
// picked on the Billing tab's Invoice Workbook view, priced by live Excel
// formulas over the backup it is built from.
//
// Sources, the same ones the invoice scripts bill from
// (localserver/scripts/billing-draft-we913-we920.js):
//   time card   Nickie's reviewed weekly card (readApprovedTimeCard: Labor
//               and Equipment tabs) is the final source of truth for hours,
//               shift, classification, per diem days and the equipment
//               roster. A week with no reviewed card yet falls back to the
//               HeavyJob cards and says so.
//   HeavyJob    the live cards (readHeavyJobCards), labor and equipment,
//               side by side with the card for the check columns.
//   JDE         the JC600 (Job Transaction Detail) export for the job, on
//               the JC600 tab exactly as JDE writes it (header in row 1):
//               read from JDE, imported on the page, or pasted in whole after
//               the download. The JDE Labor Cost tab reads it. Labor bills at the
//               person's ACTUAL wage that week × the multiplier, never the
//               rate sheet's target wage; OT × the OT factor on top; a
//               Project Director's OT at the straight rate. A person with
//               no JC600 payroll in the week billed is priced on their
//               latest JC600 week; one with none at all is not priced.
//   fuel        the rate sheet's fuel surcharge index over the quarterly
//               Gulf Coast diesel average (billingFuel.js), plus the job's
//               52300 fuel purchases as backup.
//
// Rules the formulas carry:
//   • straight time is the first OT_After hours of a person's week across
//     both VoltaGrid jobs, in date order; the sister job's share is a value
//     column ("ST hrs on other job"), so ST = MAX(0, MIN(hours, 40 − that)).
//   • per diem = the card's PD days × the per diem rate.
//   • hourly equipment bills each day's card hours with a daily minimum;
//     daily equipment bills one day rate per shift that worked that day
//     (Shifts tab, off the card's D/N column), half a rate under the
//     half-day hours; "each" bills the days used.
//   • the fuel surcharge applies to fuel-burning equipment only, at the
//     adder of the quarter before the work; a week that crosses a quarter
//     line is split into one row per fuel quarter.
//
// invoiceWorkbookModel() is pure and computes every number the formulas
// will, so the cells carry cached results (a viewer that does not
// recalculate still shows them) and the tests can check the arithmetic
// without Excel. buildInvoiceWorkbook() writes it with ExcelJS.

import moment from 'moment';
import { rulesFor } from './billingCardSource';
import { fuelRateFor, quarterSpan, shiftQuarter, parseFuelIndex } from './billingFuel';
import { sheetNameFromDesc } from './billingEquipNames';
import { OT_STRAIGHT_CLASS } from './billingPricing';
import { sheetClassOf } from './billingInvoiceExceptions';

const round2 = (n) => { const v = Number(n) || 0; return Math.round((v + Number.EPSILON * v) * 100) / 100; };
const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(n) ? n : 0; };
const norm = (s) => String(s || '').toLowerCase().replace(/&amp;/g, '&').replace(/[^a-z0-9]+/g, ' ').trim();
const ymd = (d) => String(d || '').slice(0, 10);

export const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// JDE's Director titles bill as the rate sheet's Project Director (OT at ST).
const CLASS_ALIAS = { Director: 'Project Director', 'Senior Director': 'Project Director' };
// The pay type codes at the end of a JC600 payroll line's REMARK.
export const PAY_TYPE_NAMES = { 1: 'Regular', 5: 'Overtime 1.5', 7: 'STOver40', 600: 'Truck', 610: 'Rig', 620: 'Per DiemNT' };
const PER_DIEM_PT = 620;

export const DEFAULT_WORKBOOK_RULES = { otAfter: 40, hourlyMin: 5, halfDayUnder: 5, pdRate: 175 };

/** The Sunday that ends the Monday–Sunday week a date falls in. */
export const weekEndOf = (d) => moment(d, 'YYYY-MM-DD').isoWeekday(7).format('YYYY-MM-DD');
/** The seven dates of a week, Monday first. */
export const weekDates = (we) => DOW.map((_, i) => moment(we, 'YYYY-MM-DD').subtract(6 - i, 'days').format('YYYY-MM-DD'));
/** The last `n` week endings up to the week `today` is in, newest first. */
export const recentWeekEnds = (today, n = 12) => {
    const last = weekEndOf(today);
    return Array.from({ length: n }, (_, i) => moment(last, 'YYYY-MM-DD').subtract(i * 7, 'days').format('YYYY-MM-DD'));
};
export const weekLabel = (we) => `WE ${moment(we, 'YYYY-MM-DD').format('M/D/YY')}`;
const idKey = (v) => { const s = String(v ?? '').trim(); return /^\d+$/.test(s) ? String(Number(s)) : s; };
const normUnitNo = (s) => String(s || '').toUpperCase().replace(/\s+/g, '').replace(/^PO-/, '');
const idCell = (id) => (/^\d+$/.test(String(id)) ? Number(id) : String(id));
// The card writes D, N or "D+N" (both shifts that week); a blank is days.
const shiftCode = (raw) => (/^d\s*\+?\s*n|^n\s*\+?\s*d/i.test(String(raw || '').trim()) ? 'D+N'
    : /^n/i.test(String(raw || '').trim()) ? 'N' : 'D');
// A percent (16.06) as the fraction the sheet stores (0.1606), without float dust.
const pctFrac = (pct) => Math.round((Number(pct) || 0) * 100) / 10000;
const sumDays = (days) => round2(Object.values(days || {}).reduce((a, h) => a + (Number(h) || 0), 0));

// ── Labor and equipment off one source ──

/** People off a reviewed card's Labor tab. */
export const cardPeople = (card) => (card?.labor || []).map((p) => ({
    id: idKey(p.id), name: String(p.name || '').trim(), cls: String(p.cls || '').trim(),
    shift: shiftCode(p.shiftRaw != null ? p.shiftRaw : p.shift), pd: num(p.pd), tp: num(p.tp), rr: num(p.rr),
    days: { ...(p.days || {}) },
}));

/**
 * People off HeavyJob card rows for one job: hours per date (Regular +
 * Overtime), the pay class they logged the most hours under, D / N / D+N
 * from the card shift, and adder days (one Per Diem / Truck Pay / Rig Pay
 * row per person-date). The job's ticket rules rename people the same way
 * the field tickets do (Jance Guynes).
 */
export const hjPeople = (rows, job) => {
    const by = {};
    const people = rulesFor(job).people || {};
    (rows || []).forEach((r) => {
        if (String(r.Job || job) !== String(job) || !r.Staff_ID) return;
        const id = idKey(r.Staff_ID);
        const p = by[id] || (by[id] = { id, name: String(r.Staff_Name || '').trim(), clsHrs: {}, shifts: new Set(), pd: new Set(), tp: new Set(), rr: new Set(), days: {} });
        const d = ymd(r.Work_Date);
        if (r.Record_Type === 'Regular' || r.Record_Type === 'Overtime') {
            const h = Number(r.Hours) || 0;
            if (!(h > 0)) return;
            p.days[d] = round2((p.days[d] || 0) + h);
            const c = String(r.Pay_Class_Description || '').trim();
            p.clsHrs[c] = (p.clsHrs[c] || 0) + h;
            p.shifts.add(Number(r.Shift) === 2 ? 'N' : 'D');
        } else if (r.Record_Type === 'Per Diem') p.pd.add(d);
        else if (r.Record_Type === 'Truck Pay') p.tp.add(d);
        else if (r.Record_Type === 'Rig Pay') p.rr.add(d);
    });
    return Object.values(by).filter((p) => Object.keys(p.days).length).map((p) => {
        const cls = Object.entries(p.clsHrs).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
        const over = people[p.id] || {};
        return {
            id: p.id, name: over.name || p.name, cls: over.cls || cls,
            shift: p.shifts.has('D') && p.shifts.has('N') ? 'D+N' : p.shifts.has('N') ? 'N' : 'D',
            pd: p.pd.size, tp: p.tp.size, rr: p.rr.size, days: p.days,
        };
    });
};

/**
 * A card row that is really N units: "Gang Boxes - (3)", "Threaders Rigid 1224  4- units",
 * "Mats 12X4X20' (100)". The row's hours are N × one unit's hours.
 */
export const unitCount = (desc) => {
    const m = /\(\s*(\d+)\s*\)\s*$/.exec(String(desc || '')) || /(\d+)\s*-?\s*units?\b/i.exec(String(desc || ''));
    const n = m ? Number(m[1]) : 1;
    return n > 1 && n < 1000 ? n : 1;
};
const stripCount = (desc) => String(desc || '').replace(/\s*-?\s*\(\s*\d+\s*\)\s*$/, '').replace(/\s*-?\s*\d+\s*-?\s*units?\b/i, '').replace(/\s*-\s*$/, '').trim();
/** Units off a reviewed card's Equipment tab; a row carrying a count becomes that many units at hours ÷ N. */
export const cardUnits = (card) => (card?.equipment || []).flatMap((u) => {
    const desc = String(u.desc || '').trim();
    const unit = String(u.unit || '').trim();
    const type = String(u.type || '').trim();
    const n = unitCount(desc);
    if (n === 1) return [{ desc, unit, type, days: { ...(u.days || {}) } }];
    const days = Object.fromEntries(Object.entries(u.days || {}).map(([d, h]) => [d, round2((Number(h) || 0) / n)]));
    return Array.from({ length: n }, (_, k) => ({ desc: stripCount(desc), unit: `${unit} #${k + 1} of ${n}`, type, days: { ...days }, count: n, countOf: desc }));
});

/** Units off HeavyJob card rows for one job, hours per date. */
export const hjUnits = (rows, job) => {
    const by = {};
    (rows || []).forEach((r) => {
        if (r.Record_Type !== 'Equipment' || String(r.Job || job) !== String(job)) return;
        const h = Number(r.Hours) || 0;
        if (!(h > 0)) return;
        const unit = String(r.Equipment_Code || '').trim();
        const u = by[unit] || (by[unit] = { desc: String(r.Equipment_Code_Description || '').trim(), unit, type: Number(r.Is_Rental) ? 'Rental' : 'Strike Fleet', days: {} });
        const d = ymd(r.Work_Date);
        u.days[d] = round2((u.days[d] || 0) + h);
    });
    return Object.values(by);
};

/**
 * Straight time across two jobs in one week: the first `after` hours of the
 * week are straight time whichever job they fell on, in date order (the
 * lower job number first within a day, as the invoice draft walks them).
 * Returns this job's ST and the other job's ST and hours.
 */
export const stAcrossJobs = (thisDays, otherDays, thisJob, otherJob, after = 40) => stAcrossAllJobs(thisDays, { [String(otherJob)]: otherDays || {} }, thisJob, after);
/**
 * The same across every job a person worked that week: otherByJob is
 * { [job]: { [date]: hours } } for the jobs other than this one (the sister's
 * card, the rest from JDE payroll). Returns this job's ST, the other jobs' ST
 * and hours, and a note of where the other hours were ("54026077 42h").
 */
export const stAcrossAllJobs = (thisDays, otherByJob, thisJob, after = 40) => {
    const entries = [
        ...Object.entries(thisDays || {}).map(([d, h]) => ({ d, job: String(thisJob), h: Number(h) || 0 })),
        ...Object.entries(otherByJob || {}).flatMap(([job, days]) => Object.entries(days || {}).map(([d, h]) => ({ d, job: String(job), h: Number(h) || 0 }))),
    ].filter((e) => e.h > 0).sort((a, b) => a.d.localeCompare(b.d) || a.job.localeCompare(b.job));
    let run = 0;
    let st = 0;
    let otherSt = 0;
    entries.forEach((e) => {
        const s = Math.max(0, Math.min(e.h, after - run));
        run += e.h;
        if (e.job === String(thisJob)) st += s; else otherSt += s;
    });
    const otherHrs = round2(Object.values(otherByJob || {}).reduce((a, days) => a + sumDays(days), 0));
    const otherJobs = Object.entries(otherByJob || {}).filter(([, days]) => sumDays(days) > 0).map(([job, days]) => `${job} ${sumDays(days)}h`).join('; ');
    return { st: round2(st), otherSt: round2(otherSt), otherHrs, otherJobs };
};

// ── JDE wage: the JC600 ──
//
// The JC600 export sits on the JC600 tab as JDE wrote it (A:W, header in row
// 1, nothing else, so the export's sheet pastes in whole at A1), and the JDE
// Labor Cost tab works out each line by formula. The functions here
// compute the same values the formulas do, so the cells carry cached results
// and the tests can check the arithmetic without Excel.
//   payroll line  doc type T2 with an "id - name" address number. The
//                 burden lines (address 0, "Payroll Labor Distribution",
//                 cost type 51300) are not.
//   pay type      the three digits before the company digit at the end of
//                 REMARK: "Regular Pay   001 6" → 1.
//   week ending   the Sunday on or after the GL date (a 9/30 month-end split
//                 rolls into WE 10/4).
//   base rate     amount ÷ units, ÷ 1.5 on overtime (pay type 5).

/** The export's 23 columns, A:W, headed as JDE heads them. */
export const JC600_HEADERS = [
    'JOB', 'COST CODE', 'COST TYPE', 'DESCRIPTION', 'GL DATE', 'ACTUAL\nAMOUNT', 'ACTUAL\nUNITS', 'JOURNAL\nENTRY\nEXPLANATION', 'REMARK',
    'ASSET\nNUMBER', 'ADDRESS\nNUMBER', 'PURCHASE ORDER', 'DOCUMENT\nTYPE', 'DOCUMENT\nNUMBER', 'DOCUMENT\nCOMPANY', 'POST STATUS',
    'BATCH\nTYPE', 'BATCH\nNUMBER', 'BATCH\nDATE', 'SUBLEDGER', 'SUBLEDGER TYPE', 'REFERENCE\n1', 'REFERENCE\n2',
];
// The JDE Labor Cost formulas run to this row: about three times a whole-job
// export of Block 4 at 10/26. A bigger imported export raises it.
export const JC600_LAST_ROW = 20000;
/** The JC600 tab: JDE's header in row 1, the lines from row 2. */
export const JC600_FIRST_ROW = 2;
export const NO_WAGE_NOTE = 'no JC600 wage — labor not priced';

// Excel's TRIM: spaces only, none at the ends, one between words.
const xlTrim = (s) => String(s ?? '').replace(/ +/g, ' ').replace(/^ | $/g, '');
// Excel's --"text": the number, or NaN where Excel answers #VALUE!.
const xlNum = (s) => (/^ *[-+]?(\d+\.?\d*|\.\d+) *$/.test(String(s)) ? Number(s) : NaN);
/** Excel's ROUND: half away from zero, on the 15-digit value Excel works with. */
export const xlRound = (n, d) => {
    if (!Number.isFinite(n)) return n;
    const a = Number(Math.abs(n).toPrecision(15));
    const s = String(a);
    const r = /e/i.test(s) ? Math.round(a * 10 ** d) / 10 ** d : Number(`${Math.round(Number(`${s}e${d}`))}e-${d}`);
    return n < 0 ? -r : r;
};
const xlDate = (v) => {
    if (v instanceof Date) return moment.utc(v).format('YYYY-MM-DD');
    if (typeof v === 'number' && v > 0) return moment.utc('1899-12-30', 'YYYY-MM-DD').add(Math.floor(v), 'days').format('YYYY-MM-DD');
    return null;
};
const cellNumber = (v) => (typeof v === 'number' ? v : num(v));

/**
 * One JC600 row (its 23 values, A:W) as the helper columns read it: null for
 * a blank row, { payroll: false } for any other line, else
 * { payroll, id, name, pt, we, amt, units, base, baseAmt }. id and pt are
 * null where the address or remark does not parse (the cell shows blank).
 */
export const jc600Line = (row) => {
    const v = (i) => (row || [])[i];
    if (v(0) == null || v(0) === '') return null;
    const doc = v(12);
    const addr = v(10) == null ? '' : String(v(10));
    if (!(typeof doc === 'string' && doc.toUpperCase() === 'T2' && addr.includes(' - '))) return { payroll: false };
    const at = addr.indexOf(' - ');
    const id = xlNum(addr.slice(0, at));
    const rem = xlTrim(v(8));
    const pt = rem.length >= 5 ? xlNum(rem.substr(rem.length - 5, 3)) : NaN;
    const gl = xlDate(v(4));
    const amt = cellNumber(v(5));
    const units = cellNumber(v(6));
    const f = pt === 5 ? 1.5 : 1;
    return {
        payroll: true, id: Number.isNaN(id) ? null : id, name: xlTrim(addr.slice(at + 3, at + 3 + 99)), pt: Number.isNaN(pt) ? null : pt,
        we: gl ? weekEndOf(gl) : null, amt, units, base: units !== 0 ? amt / units / f : null, baseAmt: units !== 0 ? amt / f : 0,
    };
};

/**
 * The JC600's payroll lines, by person: the wage the Labor Invoice works out
 * for a person-week and the per diem JDE paid. wageFor(we, id) →
 * { rate, week, own, note }: week is the week priced (the week billed when
 * the person has hours in it, else their latest JC600 week with hours),
 * rate the hours-weighted base rate of regular pay in it, else STOver40,
 * else overtime.
 */
export const jc600Index = (rows) => {
    const lines = (rows || []).map(jc600Line);
    const pay = lines.filter((l) => l && l.payroll);
    const byId = {};
    pay.forEach((l) => { if (l.id != null) (byId[l.id] || (byId[l.id] = [])).push(l); });
    const linesOf = (id) => byId[xlNum(idKey(id))] || [];
    const wageWeek = (we, id) => {
        const worked = linesOf(id).filter((l) => l.units !== 0 && l.we);
        if (worked.some((l) => l.we === we)) return we;
        return worked.reduce((a, l) => (a == null || l.we > a ? l.we : a), null);
    };
    const wageFor = (we, id) => {
        const week = wageWeek(we, id);
        let rate = null;
        if (week) {
            const inWeek = linesOf(id).filter((l) => l.we === week);
            [1, 7, 5].some((t) => {
                let amt = 0;
                let hrs = 0;
                inWeek.forEach((l) => { if (l.pt === t) { amt += l.baseAmt; hrs += l.units; } });
                if (hrs === 0) return false;
                rate = xlRound(amt / hrs, 3);
                return true;
            });
        }
        if (rate == null) return { rate: null, week, own: false, note: NO_WAGE_NOTE };
        const own = week === we;
        return { rate, week, own, note: own ? `JC600 ${weekLabel(we)}` : `no JC600 payroll this week: ${weekLabel(week)} wage` };
    };
    const perDiemPaid = (we, id) => linesOf(id).filter((l) => l.we === we && l.pt === PER_DIEM_PT).reduce((a, l) => a + l.amt, 0);
    const perDiemPeople = (we) => pay.filter((l) => l.we === we && l.pt === PER_DIEM_PT && l.id != null);
    const totals = {
        lines: pay.length,
        hrs: pay.reduce((a, l) => a + l.units, 0),
        amt: pay.reduce((a, l) => a + l.amt, 0),
        baseAmt: pay.reduce((a, l) => a + l.baseAmt, 0),
        latest: pay.reduce((a, l) => (l.we && (a == null || l.we > a) ? l.we : a), null),
    };
    return { lines, wageFor, perDiemPaid, perDiemPeople, totals };
};

const rawCell = (v) => {
    if (v == null || v instanceof Date || typeof v !== 'object') return v ?? null;
    if (v.richText) return v.richText.map((t) => t.text).join('');
    if (v.text != null) return v.text;
    if ('result' in v) return v.result ?? null;
    return null;
};

/**
 * A JC600 export read in the browser: the sheet with JDE's header row (JOB …
 * REFERENCE 2, A:W) and every row under it as its 23 raw values.
 * { found, header, rows, jobs } or { found: false, reason }.
 */
export const parseJc600Workbook = (wb) => {
    const key = (s) => String(s ?? '').replace(/\s+/g, ' ').trim().toUpperCase();
    const want = JC600_HEADERS.map(key);
    for (const ws of wb.worksheets) {
        for (let r = 1; r <= Math.min(ws.rowCount, 10); r += 1) {
            const header = want.map((_, i) => cellText(ws.getCell(r, i + 1).value));
            if (header.some((h, i) => key(h) !== want[i])) continue;
            const extra = cellText(ws.getCell(r, want.length + 1).value).trim();
            if (extra) return { found: false, reason: `it has a column past W ("${extra}") that would land on the formulas` };
            const rows = [];
            let last = 0;
            for (let rr = r + 1; rr <= ws.rowCount; rr += 1) {
                const vals = want.map((_, i) => rawCell(ws.getCell(rr, i + 1).value));
                rows.push(vals);
                if (vals.some((x) => x != null && x !== '')) last = rows.length;
            }
            rows.length = last;
            const jobs = [...new Set(rows.map((x) => String(x[0] ?? '').trim()).filter(Boolean))];
            return { found: true, header, rows, jobs };
        }
    }
    return { found: false, reason: 'no JC600 header row (JOB, COST CODE, … REFERENCE 2)' };
};

/** The Summary's two wage checks; the workbook writes them as formulas that give the same words. */
const latestText = (latest) => (latest ? `${weekLabel(latest)}.` : 'none — paste the JC600 export into the JC600 tab at A1.');
export const unpricedCheck = (n, latest) => (n
    ? `${n} person-week${n === 1 ? '' : 's'} with no JC600 wage, so not priced (see Wage note on Labor Invoice). Latest JC600 week: ${latestText(latest)}`
    : 'Every person-week has a JC600 wage.');
export const carriedCheck = (n, latest) => (n
    ? `${n} person-week${n === 1 ? '' : 's'} priced on the wage of another JC600 week (see Wage note on Labor Invoice): the JC600 has no payroll for them in the week billed. Latest JC600 week: ${latestText(latest)}`
    : 'No person-week is priced on another week\'s wage.');

// ── Equipment pricing ──

/** Billed quantity of one row's seven days, as the Billed qty formula computes it. */
export const equipQty = (uom, days7, shifts7, rules = DEFAULT_WORKBOOK_RULES) => {
    const hrs = days7.map((h) => Number(h) || 0);
    if (uom === 'hr') return round2(hrs.reduce((a, h) => a + (h > 0 ? (h < rules.hourlyMin ? rules.hourlyMin : h) : 0), 0));
    if (uom === 'day') {
        return round2(hrs.reduce((a, h, i) => {
            const s = Number(shifts7[i]) || 0;
            if (h >= rules.halfDayUnder) return a + (s === 0 ? 1 : s);
            return a + (h > 0 ? 0.5 : 0);
        }, 0));
    }
    return hrs.filter((h) => h > 0).length;
};

/** Shifts that worked each day of a week (0, 1 or 2) off the card's people. */
export const shiftsOf = (people, dates) => dates.map((d) => {
    const on = (p) => (Number(p.days[d]) || 0) > 0;
    const day = people.some((p) => on(p) && (p.shift === 'D' || p.shift === 'D+N'));
    const night = people.some((p) => on(p) && (p.shift === 'N' || p.shift === 'D+N'));
    return (day ? 1 : 0) + (night ? 1 : 0);
});

const fuelIndexOf = (fuelRows) => {
    const { below, points, ext } = parseFuelIndex(fuelRows);
    const step = ext && ext.step > 0 ? ext.step : 0.05;
    const inc = ext && ext.step > 0 ? ext.inc : 0.67;
    const threshold = below ? below.price : points.length ? points[0].price - step : 3.75;
    return { threshold, step, inc, parsed: Boolean(below && ext) };
};

// ── The model ──

/**
 * input: {
 *   job, jobName, client, weeks: ['YYYY-MM-DD' week endings],
 *   cards: { [job]: { [we]: readApprovedTimeCard result | null } }  (this job + its sister)
 *   hj:    { [we]: { data: [...readHeavyJobCards rows], error? } }
 *   jc600: { name, header, rows } | null        an imported JC600 export (parseJc600Workbook);
 *                                              null leaves the tab for a paste
 *   cfg:   { m, otf }                          the job's rate config
 *   rules: { otAfter, hourlyMin, halfDayUnder, pdRate }
 *   equipRows, fuelRows, fuelSettings          the job's merged rate schedule + settings.fuel
 *   namer: (unit, desc) => { name, via }       billingEquipNames equipNamer, optional
 *   equipAs                                    the Service Order's row swaps, used without a namer
 *   fuelTxns: priced 52300 cost lines          (billingPricing rows)
 *   otherHours: { [we]: { [id]: { [job]: { [date]: hrs } } } }   hours on the other VoltaGrid jobs
 *               from JDE payroll (billingInvoiceExceptions otherHoursFromPay); with it, straight
 *               time counts every job a person worked, not only the sister's card
 *   laborRows:  the job's rate sheet labor rows [{ name, wage }], for the classification lookups below
 *   masterClasses: { [id]: billCls }   each person's "Rate Sheet Billing Classification" off the
 *               Attendance & Force Report master. When it is a rate sheet line the person bills under
 *               it, whatever the card says (Seth 11/2: the master is the billing classification)
 *   classRules: [{ card, as }]   a card (or master) classification billed under a rate sheet line ('' keeps the wording)
 *   unitRules:  [{ match, as, skip }]   a card unit (description or unit no) billed as a rate sheet
 *               item, or left off the invoice
 *   preparedBy, preparedAt
 * }
 */
export const invoiceWorkbookModel = (input) => {
    const job = String(input.job);
    const sister = rulesFor(job).sister || '';
    const weeks = [...new Set(input.weeks || [])].sort();
    const rules = { ...DEFAULT_WORKBOOK_RULES, ...(input.rules || {}) };
    const cfg = { m: num(input.cfg?.m) || 1.633, otf: num(input.cfg?.otf) || 1.4 };
    const equipRows = input.equipRows || [];
    const flags = [];
    const cardOf = (j, we) => ((input.cards || {})[j] || {})[we] || null;
    const hjOf = (we) => ((input.hj || {})[we] || {});
    const otherHours = input.otherHours || null;
    const classRules = {};
    (input.classRules || []).forEach((r) => { if (r && String(r.card || '').trim()) classRules[norm(r.card)] = { as: String(r.as || '').trim(), card: String(r.card).trim() }; });
    const laborRows = input.laborRows || [];
    const masterClasses = input.masterClasses || null;
    // The classification a person bills under, in this order: the master's rate sheet
    // line; a rule on the master's or the card's wording; the card's wording (through the
    // alias map, then the sheet's own spelling).
    const classOf = (cls, id) => {
        const mc = masterClasses ? String(masterClasses[idKey(id)] || '').trim() : '';
        if (mc) {
            const line = sheetClassOf(mc, laborRows);
            if (line) return { cls: line, rule: 'master', masterCls: mc };
            const r = classRules[norm(mc)];
            if (r && r.as) return { cls: r.as, rule: 'as', masterCls: mc };
        }
        const r = classRules[norm(cls)];
        if (r) return { cls: r.as || cls, rule: r.as ? 'as' : 'keep', masterCls: mc };
        return { cls: sheetClassOf(cls, laborRows) || CLASS_ALIAS[cls] || cls, rule: '', masterCls: mc };
    };
    const unitRules = (input.unitRules || []).filter((r) => r && String(r.match || '').trim()).map((r) => ({ match: norm(r.match), matchUnit: normUnitNo(r.match), as: String(r.as || '').trim(), skip: Boolean(r.skip) }));
    const unitRuleFor = (desc, unit) => unitRules.find((r) => (r.match && (norm(desc) === r.match || norm(desc.replace(/\s*\(Rental\)\s*$/i, '')) === r.match)) || (r.matchUnit && normUnitNo(unit) === r.matchUnit)) || null;

    const weekInfo = [];
    const tcLabor = [];
    const tcEquip = [];
    const hjLabor = [];
    const hjEquip = [];
    const shifts = [];
    const peopleByWeek = {};
    const sisterByWeek = {};

    weeks.forEach((we) => {
        const dates = weekDates(we);
        const card = cardOf(job, we);
        const hjRows = hjOf(we).data || [];
        const hjErr = hjOf(we).error || '';
        const info = { we, dates, laborBasis: 'none', equipBasis: 'none', file: card?.found ? card.file?.name || '' : '' };

        // Labor: the reviewed card, else HeavyJob.
        let people = [];
        if (card?.found && (card.labor || []).length) {
            people = cardPeople(card);
            info.laborBasis = 'card';
        } else {
            people = hjPeople(hjRows, job);
            if (people.length) {
                info.laborBasis = 'hj';
                flags.push(`${weekLabel(we)}: no reviewed time card yet — labor is off the HeavyJob cards (classification is the HeavyJob pay class).`);
            } else {
                flags.push(hjErr
                    ? `${weekLabel(we)}: no reviewed time card, and the HeavyJob cards were not read (${hjErr}) — no labor or equipment this week.`
                    : `${weekLabel(we)}: no labor on a reviewed time card or the HeavyJob cards.`);
            }
        }
        peopleByWeek[we] = people;
        const basisText = (b) => (b === 'card' ? `Reviewed time card${info.file ? `: ${info.file}` : ''}` : b === 'hj' ? 'HeavyJob cards (no reviewed card)' : '');
        people.slice().sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)).forEach((p) => {
            tcLabor.push({ we, basis: basisText(info.laborBasis), ...p, days7: dates.map((d) => Number(p.days[d]) || 0), total: sumDays(p.days) });
        });

        // Equipment: the reviewed card's Equipment tab, else HeavyJob. A card
        // read by a reader that predates the Equipment tab has no
        // `equipment` at all — HeavyJob then, and say so.
        let units = [];
        if (card?.found && Array.isArray(card.equipment)) {
            units = cardUnits(card);
            info.equipBasis = 'card';
            const hjU = hjUnits(hjRows, job);
            if (!units.length && hjU.length) flags.push(`${weekLabel(we)}: the reviewed time card lists no equipment; HeavyJob has ${hjU.length} unit${hjU.length === 1 ? '' : 's'} on the cards.`);
        } else {
            units = hjUnits(hjRows, job);
            if (units.length) {
                info.equipBasis = 'hj';
                if (card?.found) flags.push(`${weekLabel(we)}: the time card's Equipment tab was not read — equipment is off the HeavyJob cards.`);
            }
        }
        const eBasis = info.equipBasis === 'card' ? basisText('card') : info.equipBasis === 'hj' ? 'HeavyJob cards' : '';
        units.slice().sort((a, b) => a.desc.localeCompare(b.desc) || a.unit.localeCompare(b.unit)).forEach((u) => {
            const days7 = dates.map((d) => Number(u.days[d]) || 0);
            tcEquip.push({ we, basis: eBasis, ...u, days7, total: round2(days7.reduce((a, h) => a + h, 0)), n: days7.filter((h) => h > 0).length });
        });

        // The HeavyJob backup, this job only.
        hjRows.filter((r) => String(r.Job || job) === job).forEach((r) => {
            const shift = Number(r.Shift) === 2 ? 'Night' : Number(r.Shift) === 1 ? 'Day' : '';
            if (r.Record_Type === 'Equipment') {
                hjEquip.push({
                    date: ymd(r.Work_Date), we, card: String(r.Card_ID || ''), foreman: r.Foreman_Name || '', shift, unit: String(r.Equipment_Code || '').trim(),
                    desc: r.Equipment_Code_Description || '', rental: Number(r.Is_Rental) ? 'Y' : 'N', cc: r.Cost_Code || '', ccd: r.Cost_Code_Description || '', hrs: round2(r.Hours),
                });
            } else if (r.Staff_ID) {
                hjLabor.push({
                    date: ymd(r.Work_Date), we, card: String(r.Card_ID || ''), foreman: r.Foreman_Name || '', shift, id: idKey(r.Staff_ID), name: r.Staff_Name || '',
                    payClass: r.Pay_Class_Description || '', cc: r.Cost_Code || '', ccd: r.Cost_Code_Description || '', type: r.Record_Type || '', hrs: round2(r.Hours),
                });
            }
        });
        if (hjErr && people.length) flags.push(`${weekLabel(we)}: HeavyJob cards not read (${hjErr}) — the HeavyJob check columns are blank.`);

        // The sister job's hours, for straight time across both jobs.
        if (sister) {
            const sCard = cardOf(sister, we);
            const sPeople = sCard?.found && (sCard.labor || []).length ? cardPeople(sCard) : hjPeople(hjRows, sister);
            const by = {};
            sPeople.forEach((p) => {
                const x = by[p.id] || (by[p.id] = {});
                Object.entries(p.days).forEach(([d, h]) => { x[d] = round2((x[d] || 0) + (Number(h) || 0)); });
            });
            sisterByWeek[we] = { by, basis: sCard?.found ? 'card' : sPeople.length ? 'hj' : 'none' };
            if (!sCard?.found && hjOf(we).sisterError && people.length) {
                flags.push(`${weekLabel(we)}: ${sister} has no reviewed card and its HeavyJob cards were not read (${hjOf(we).sisterError}) — straight time does not count hours on ${sister}; overtime may be short.`);
            }
        }

        shifts.push({ we, s: shiftsOf(people, dates) });
        weekInfo.push(info);
    });

    // ── JDE payroll: the JC600, whole, as exported ──
    const jc = input.jc600 && Array.isArray(input.jc600.rows) ? input.jc600 : null;
    const pays = jc600Index(jc ? jc.rows : []);
    const weekSet = new Set(weeks);
    // Room for the export to double before the formulas run out.
    const jcLast = Math.max(JC600_LAST_ROW, Math.ceil((R0 + 2 * (jc ? jc.rows.length : 0)) / 10000) * 10000);

    // ── Labor Invoice: one row per person per week ──
    const labor = [];
    weeks.forEach((we) => {
        const people = peopleByWeek[we] || [];
        const merged = {};
        people.forEach((p) => {
            const x = merged[p.id] || (merged[p.id] = { ...p, days: {} });
            Object.entries(p.days).forEach(([d, h]) => { x.days[d] = round2((x.days[d] || 0) + (Number(h) || 0)); });
        });
        const info = weekInfo.find((w) => w.we === we);
        Object.values(merged).sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)).forEach((p) => {
            const hrs = sumDays(p.days);
            // Hours on the other jobs: payroll's for every other VoltaGrid job, the sister's card over payroll's for the sister.
            const otherByJob = { ...(((otherHours || {})[we] || {})[p.id] || {}) };
            if (sister && sisterByWeek[we] && (sisterByWeek[we].by[p.id] || sisterByWeek[we].basis === 'card')) otherByJob[sister] = sisterByWeek[we].by[p.id] || {};
            delete otherByJob[job];
            const sis = stAcrossAllJobs(p.days, otherByJob, job, rules.otAfter);
            const st = round2(Math.max(0, Math.min(hrs, rules.otAfter - sis.otherSt)));
            const ot = round2(hrs - st);
            const co = classOf(p.cls, p.id);
            const cls = co.cls;
            const w = pays.wageFor(we, p.id);
            const wage = w.rate;
            const stRate = wage == null ? null : round2(wage * cfg.m);
            const otRate = wage == null ? null : cls === OT_STRAIGHT_CLASS ? stRate : round2(wage * cfg.m * cfg.otf);
            const stAmt = stRate == null ? 0 : round2(st * stRate);
            const otAmt = otRate == null ? 0 : round2(ot * otRate);
            const hjHrs = round2(hjLabor.filter((r) => r.we === we && r.id === p.id).reduce((a, r) => a + r.hrs, 0));
            labor.push({
                we, id: p.id, name: p.name, cls, cardCls: p.cls, masterCls: co.masterCls || '', clsRule: co.rule, shift: p.shift, basis: info.laborBasis === 'card' ? 'Time card' : 'HeavyJob',
                hrs, otherHrs: sis.otherHrs, otherSt: sis.otherSt, otherJobs: sis.otherJobs, st, ot, wage, wageWeek: w.week, wageOwn: w.own, wageNote: w.note,
                stRate, otRate, stAmt, otAmt, total: round2(stAmt + otAmt), hjHrs, diff: round2(hrs - hjHrs),
            });
        });
    });
    if (otherHours) {
        weeks.forEach((we) => {
            const n = Object.keys(otherHours[we] || {}).length;
            if (!n && (peopleByWeek[we] || []).length) flags.push(`${weekLabel(we)}: JDE payroll has no hours on the other VoltaGrid jobs for this week yet (not posted, or nobody worked another job) — straight time counts only this job's card${sister ? ` and ${sister}'s` : ''}; overtime may be short until payroll posts.`);
        });
    }
    // Formulas on the Summary (they follow a paste); `n` counts what they count.
    const unpriced = labor.filter((l) => l.wage == null).length;
    const carried = labor.filter((l) => l.wage != null && !l.wageOwn).length;
    flags.push({ key: 'unpriced', n: unpriced, text: unpricedCheck(unpriced, pays.totals.latest) });
    flags.push({ key: 'carried', n: carried, text: carriedCheck(carried, pays.totals.latest) });
    // A week whose cards were not read has nothing to differ from.
    const hjDiff = labor.filter((l) => l.basis === 'Time card' && Math.abs(l.diff) > 0.001 && !hjOf(l.we).error);
    if (hjDiff.length) flags.push(`${hjDiff.length} person-week${hjDiff.length === 1 ? '' : 's'} where the time card and HeavyJob hours differ (Labor Invoice, Card − HJ). The time card bills.`);

    // ── Per Diem ──
    const perDiem = [];
    weeks.forEach((we) => {
        const seen = {};
        (peopleByWeek[we] || []).forEach((p) => {
            const x = seen[p.id] || (seen[p.id] = { we, id: p.id, name: p.name, cls: classOf(p.cls, p.id).cls, days: 0 });
            x.days += p.pd;
        });
        pays.perDiemPeople(we).forEach((l) => {
            const id = String(l.id);
            if (!seen[id]) seen[id] = { we, id, name: l.name, cls: '', days: 0 };
        });
        Object.values(seen).forEach((x) => {
            const jdePaid = round2(pays.perDiemPaid(we, x.id));
            const hjDays = hjLabor.filter((r) => r.we === we && r.id === x.id && r.type === 'Per Diem').length;
            if (!x.days && !jdePaid && !hjDays) return;
            perDiem.push({ ...x, rate: rules.pdRate, amt: round2(x.days * rules.pdRate), jdePaid, hjDays, diff: x.days - hjDays });
        });
    });
    perDiem.sort((a, b) => a.we.localeCompare(b.we) || a.name.localeCompare(b.name));

    // ── Equipment Invoice: one row per unit per week (per fuel quarter) ──
    const fuelCtx = { settings: input.fuelSettings, rows: input.fuelRows };
    const equip = [];
    const unnamed = [];
    const gas = [];
    const tcRowOf = new Map(); // tcEquip index by object
    tcEquip.forEach((t, i) => tcRowOf.set(t, i));
    weeks.forEach((we) => {
        const dates = weekDates(we);
        const sh = (shifts.find((s) => s.we === we) || { s: [0, 0, 0, 0, 0, 0, 0] }).s;
        // One invoice row per unit: a card that lists a unit twice adds up.
        const groups = {};
        tcEquip.filter((t) => t.we === we).forEach((t, i) => {
            // A card that lists one unit twice adds up; two rows with no unit number ("Strike Fleet",
            // blank) are two units (71's two Honda generators), not one at double the hours.
            const k = /[0-9]/.test(t.unit) ? `${norm(t.desc)}|${t.unit.toUpperCase()}` : `${norm(t.desc)}|${t.unit.toUpperCase()}|${i}`;
            (groups[k] || (groups[k] = [])).push(t);
        });
        Object.values(groups).forEach((list) => {
            const t = list[0];
            // A rule may name the card's row as written, count and all ("Gang Boxes - (3)"), or the unit it became.
            const ur = unitRuleFor(t.desc, t.unit) || (t.countOf ? unitRuleFor(t.countOf, '') : null);
            const hit = ur ? (ur.skip ? { name: '', via: 'skip' } : { name: ur.as, via: 'exception' })
                : typeof input.namer === 'function' ? input.namer(t.unit, t.desc) : { name: sheetNameFromDesc(t.desc, equipRows, input.equipAs), via: 'rule' };
            const skipped = Boolean(ur && ur.skip);
            const item = hit?.name || '';
            const row = item ? equipRows.find((r) => norm(r.name) === norm(item)) : null;
            if (!row && !skipped) unnamed.push(`${t.desc}${t.unit ? ` (${t.unit})` : ''}`);
            if (item === '4 Gas Monitor') gas.push(weekLabel(we));
            const days7 = dates.map((_, i) => round2(list.reduce((a, x) => a + x.days7[i], 0)));
            // Days by the fuel quarter their adder comes from.
            const parts = {};
            dates.forEach((d, i) => {
                if (!(days7[i] > 0)) return;
                const fr = fuelRateFor(d, fuelCtx);
                (parts[fr.q] || (parts[fr.q] = { fr, idx: [] })).idx.push(i);
            });
            const partList = Object.values(parts).sort((a, b) => a.idx[0] - b.idx[0]);
            const hjHrs = round2(hjEquip.filter((r) => r.we === we && t.unit && r.unit.toUpperCase() === t.unit.toUpperCase()).reduce((a, r) => a + r.hrs, 0));
            partList.forEach((part, pi) => {
                const pdays = days7.map((h, i) => (part.idx.includes(i) ? h : 0));
                const rate = row ? num(row.rate) : null;
                const uom = row ? row.uom : '';
                const fu = row ? row.fu !== false : false;
                // No rate sheet row: no UOM, and the formula bills nothing.
                const qty = row ? equipQty(uom, pdays, sh, rules) : 0;
                const amt = rate == null ? 0 : round2(qty * rate);
                const fpct = fu && !part.fr.missing ? pctFrac(part.fr.pct) : 0;
                const fuel = round2(amt * fpct);
                const cardHrs = round2(pdays.reduce((a, h) => a + h, 0));
                equip.push({
                    we, desc: t.desc, unit: t.unit, type: t.type, item: row ? row.name : '', via: hit?.via || '', skipped, uom, rate, fu,
                    days7: pdays, refs: list.map((x) => tcRowOf.get(x)), part: part.idx, cardHrs, n: pdays.filter((h) => h > 0).length,
                    qty, amt, fq: part.fr.q, fpct, fuel, total: round2(amt + fuel),
                    hjHrs: pi === 0 && t.unit ? hjHrs : null, split: partList.length > 1,
                    note: skipped ? 'left off the invoice (unit exception)' : !row ? (item ? `"${item}" is not on the job's rate sheet` : 'no rate sheet item — map the unit in Rates & Rules') : item === '4 Gas Monitor' ? 'bills per person-day on site — check the quantity' : '',
                });
            });
        });
    });
    if (unnamed.length) flags.push(`${unnamed.length} unit${unnamed.length === 1 ? '' : 's'} with no rate sheet item, billed $0: ${[...new Set(unnamed)].slice(0, 8).join('; ')}.`);
    if (gas.length) flags.push(`4 gas monitors on ${[...new Set(gas)].join(', ')}: the invoices bill them per person-day on site, not per shift — check the quantity.`);

    // ── Fuel ──
    const idx = fuelIndexOf(input.fuelRows);
    if (!idx.parsed) flags.push('The fuel index was not read off the rate sheet — threshold $3.75, $0.05 steps, 0.67% a step assumed.');
    const qs = [...new Set(equip.map((e) => e.fq).filter(Boolean))].sort();
    const fuelQ = qs.map((q) => {
        // Any date in the quarter after `q` prices at q's average.
        const sample = fuelRateFor(`${shiftQuarter(q, 1).slice(0, 4)}-${String((Number(shiftQuarter(q, 1).slice(-1)) - 1) * 3 + 1).padStart(2, '0')}-01`, fuelCtx);
        return { q, applies: quarterSpan(shiftQuarter(q, 1)), price: sample.price, pct: sample.missing ? 0 : pctFrac(sample.pct), missing: sample.missing };
    });
    fuelQ.filter((f) => f.missing).forEach((f) => flags.push(`No Gulf Coast diesel average for ${f.q} yet — work in ${f.applies} carries no fuel surcharge until it is entered on the Fuel tab.`));
    const weekRange = { from: weeks.length ? weekDates(weeks[0])[0] : '', to: weeks.length ? weeks[weeks.length - 1] : '' };
    const fuelTx = (input.fuelTxns || [])
        .filter((t) => weekSet.has(weekEndOf(ymd(t.date))))
        .map((t) => ({
            date: ymd(t.date), we: weekEndOf(ymd(t.date)), vendor: t.who || '', po: t.po ? String(num(t.po)) : '', line: t.pol || '', desc: t.ds || '',
            cc: t.cc || '', cost: round2(t.cost), billable: t.nb ? 'No' : 'Yes', bill: t.nb ? 0 : round2(t.bill), inv: t.inv || '',
        }))
        .sort((a, b) => a.date.localeCompare(b.date) || a.vendor.localeCompare(b.vendor));

    // ── Roll-ups ──
    const sum = (a, k) => round2(a.reduce((s, x) => s + (Number(typeof k === 'function' ? k(x) : x[k]) || 0), 0));
    const totals = {
        hrs: sum(labor, 'hrs'), st: sum(labor, 'st'), ot: sum(labor, 'ot'), labor: sum(labor, 'total'),
        pdDays: sum(perDiem, 'days'), pd: sum(perDiem, 'amt'), equip: sum(equip, 'amt'), fuel: sum(equip, 'fuel'),
    };
    totals.equipTotal = round2(totals.equip + totals.fuel);
    totals.total = round2(totals.labor + totals.pd + totals.equipTotal);
    const byWeek = weeks.map((we) => {
        const l = labor.filter((x) => x.we === we);
        const e = equip.filter((x) => x.we === we);
        const info = weekInfo.find((w) => w.we === we);
        const r = { we, hrs: sum(l, 'hrs'), labor: sum(l, 'total'), pd: sum(perDiem.filter((x) => x.we === we), 'amt'), equip: sum(e, 'amt'), fuel: sum(e, 'fuel'), laborBasis: info.laborBasis, equipBasis: info.equipBasis, file: info.file, people: l.length, units: new Set(e.map((x) => `${x.desc}|${x.unit}`)).size };
        r.total = round2(r.labor + r.pd + r.equip + r.fuel);
        return r;
    });
    const byClass = [...new Set(labor.map((l) => l.cls))].sort().map((cls) => {
        const l = labor.filter((x) => x.cls === cls);
        return { cls, st: sum(l, 'st'), ot: sum(l, 'ot'), hrs: sum(l, 'hrs'), amt: sum(l, 'total') };
    });
    const byItem = [...new Set(equip.filter((e) => e.item).map((e) => e.item))].sort().map((item) => {
        const e = equip.filter((x) => x.item === item);
        return { item, uom: e[0].uom, rate: e[0].rate, qty: sum(e, 'qty'), amt: sum(e, 'amt'), fuel: sum(e, 'fuel') };
    });

    return {
        job, jobName: input.jobName || '', client: input.client || '', sister, otherSource: otherHours ? 'payroll' : sister ? 'sister' : '', weeks, range: weekRange, rules, cfg,
        preparedBy: input.preparedBy || '', preparedAt: input.preparedAt || moment().format('YYYY-MM-DD HH:mm'),
        jc600: jc ? { name: jc.name || '', header: jc.header || JC600_HEADERS, rows: jc.rows, lines: pays.lines, totals: pays.totals } : null,
        jcLast,
        weekInfo, tcLabor, tcEquip, hjLabor, hjEquip, shifts, labor, perDiem, equip, fuelIdx: idx, fuelQ, fuelTx,
        totals, byWeek, byClass, byItem,
        // checks: every line for the Summary, the wage ones with the count their formula gives;
        // flags: what needs a look (a wage check only when its count is not zero).
        checks: flags.map((f) => (typeof f === 'string' ? { text: f } : f)),
        flags: flags.filter((f) => typeof f === 'string' || f.n > 0).map((f) => (typeof f === 'string' ? f : f.text)),
    };
};

export const workbookFileName = (model) => {
    const f = (d) => moment(d, 'YYYY-MM-DD').format('M.D.YY');
    return `Invoice_Backup_${model.job}_${model.range.from ? `${f(model.range.from)}-${f(model.range.to)}` : 'no-weeks'}.xlsx`;
};

// ── ExcelJS ──

const SH = {
    sum: 'Summary', li: 'Labor Invoice', pd: 'Per Diem', ei: 'Equipment Invoice', fuel: 'Fuel', shifts: 'Shifts',
    tcl: 'Time Card - Labor', tce: 'Time Card - Equipment', hjl: 'HJ Labor', hje: 'HJ Equipment', jc: 'JC600', jde: 'JDE Labor Cost',
};
const HDR = 5;
const R0 = 6;
const MONEY = '#,##0.00;(#,##0.00);"-"';
const HOURS = '#,##0.00;(#,##0.00);"-"';
const RATE = '#,##0.00';
const WAGE = '#,##0.000';
const PCT = '0.00%';
const DATE = 'm/d/yy';
const NAVY = 'FF1D518C';
const INK = 'FF0A0A0A';
const GREY = 'FF525252';
const MUTED = 'FF737373';
const LINE = 'FFE5E5E5';
const SUBTLE = 'FFFAFAFA';
const TINT = 'FFE8EEF5';
const BAD_TINT = 'FFFBEAEA';
const BAD_TEXT = 'FFB02329';
const WARN_TINT = 'FFFDF4DC';

const colL = (n) => { let s = ''; let x = n; while (x > 0) { const m = (x - 1) % 26; s = String.fromCharCode(65 + m) + s; x = Math.floor((x - 1) / 26); } return s; };
const q = (sheet) => `'${sheet}'`;
const rng = (sheet, col, n) => `${q(sheet)}!$${col}$${R0}:$${col}$${R0 + Math.max(n, 1) - 1}`;
const xDate = (d) => (d ? new Date(`${d}T00:00:00Z`) : null);
const fx = (formula, result) => ({ formula, result: result == null ? undefined : result });
const str = (s) => `"${String(s).replace(/"/g, '""')}"`;

function sheetHead(ws, title, sub, note) {
    ws.getCell('A1').value = title;
    ws.getCell('A1').font = { bold: true, size: 14, color: { argb: INK } };
    ws.getCell('A2').value = sub;
    ws.getCell('A2').font = { size: 11, color: { argb: GREY } };
    if (note) {
        ws.getCell('A3').value = note;
        ws.getCell('A3').font = { italic: true, size: 10, color: { argb: MUTED } };
    }
}

function tableHeader(ws, row, cols) {
    const r = ws.getRow(row);
    cols.forEach((c, i) => {
        const cell = r.getCell(i + 1);
        cell.value = c.h;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
        cell.alignment = { vertical: 'middle', horizontal: c.right ? 'right' : 'left', wrapText: true };
        if (c.w) ws.getColumn(i + 1).width = c.w;
    });
    r.height = 30;
}

function writeRow(ws, row, cols, values, opts = {}) {
    const r = ws.getRow(row);
    values.forEach((v, i) => {
        const cell = r.getCell(i + 1);
        cell.value = v === undefined ? null : v;
        const c = cols[i] || {};
        if (c.fmt) cell.numFmt = c.fmt;
        if (c.right) cell.alignment = { horizontal: 'right' };
        cell.border = { bottom: { style: 'thin', color: { argb: LINE } } };
        if (c.input || (opts.input || []).includes(i)) {
            cell.font = { color: { argb: NAVY } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TINT } };
        }
        if (opts.bold) cell.font = { ...(cell.font || {}), bold: true };
        if (opts.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.fill } };
    });
    return r;
}

function totalRow(ws, row, cols, values) {
    const r = writeRow(ws, row, cols, values, { bold: true, fill: SUBTLE });
    r.eachCell((c) => { c.border = { top: { style: 'thin', color: { argb: GREY } }, bottom: { style: 'double', color: { argb: GREY } } }; });
    return r;
}

const flagCell = (cell, tone) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tone === 'bad' ? BAD_TINT : WARN_TINT } };
    if (tone === 'bad') cell.font = { ...(cell.font || {}), color: { argb: BAD_TEXT } };
};

// ── The reviewed time card, copied in ──

const cellText = (v) => {
    if (v == null) return '';
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === 'object') {
        if (v.richText) return v.richText.map((t) => t.text).join('');
        if (v.text != null) return String(v.text);
        if ('result' in v) return v.result == null ? '' : String(v.result);
        return '';
    }
    return String(v);
};
const cellNum = (v) => {
    if (v && typeof v === 'object' && !(v instanceof Date)) return num(v.result);
    return typeof v === 'number' ? v : num(v);
};
const BAD_SHEET_CHARS = /[\\/?*[\]:]/g;

/** A copied card tab's name: "TC 10.11.26 Labor", unique, at most 31 characters. */
export const cardTabName = (we, name, taken) => {
    const base = `TC ${moment(we, 'YYYY-MM-DD').format('M.D.YY')} ${String(name).replace(BAD_SHEET_CHARS, ' ')}`.slice(0, 31).trim();
    let out = base;
    for (let i = 2; taken.has(out.toLowerCase()); i += 1) out = `${base.slice(0, 27).trim()} ${i}`;
    taken.add(out.toLowerCase());
    return out;
};

/** Point a formula's sheet references at the copied tabs' new names. */
export const renameSheetRefs = (formula, map) => {
    let out = String(formula || '');
    Object.entries(map).forEach(([from, to]) => {
        const esc = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        out = out.replace(new RegExp(`(^|[^A-Za-z0-9_.'])(?:'${esc}'|${esc})!`, 'g'), `$1'${to.replace(/'/g, "''")}'!`);
    });
    return out;
};

/**
 * Where a reviewed card keeps its numbers, read off its own headers the way
 * readApprovedTimeCard does: the Labor tab's row per person (Employee, ID,
 * PD/TP/RR, a column per date) and the Equipment tab's row per unit with
 * hours. { labor: { sheet, cols, rows: [{ id, name, r }] } | null,
 *          equip: { sheet, cols, rows: [{ desc, unit, r }] } | null }
 */
export const cardLayout = (wb, we) => {
    const year = Number(String(we).slice(0, 4)) || new Date().getUTCFullYear();
    const header = (ws, test) => {
        for (let r = 1; r <= Math.min(ws.rowCount, 12); r += 1) {
            const texts = [];
            ws.getRow(r).eachCell({ includeEmpty: true }, (c, col) => { texts[col] = cellText(c.value).replace(/\s+/g, ' ').trim(); });
            const idx = (re) => texts.findIndex((t) => re.test(t || ''));
            const dates = {};
            texts.forEach((t, col) => {
                const mt = /(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(t || '');
                if (!mt) return;
                let y = mt[3] ? Number(mt[3]) : year;
                if (y < 100) y += 2000;
                dates[`${y}-${String(mt[1]).padStart(2, '0')}-${String(mt[2]).padStart(2, '0')}`] = col;
            });
            if (Object.keys(dates).length && test(idx)) return { r, idx, dates };
        }
        return null;
    };
    const out = { labor: null, equip: null };
    const lab = wb.worksheets.find((w) => /^labor$/i.test(w.name)) || wb.worksheets[0];
    const lh = lab && header(lab, (idx) => idx(/^id$/i) > 0 && idx(/^employee$/i) > 0);
    if (lh) {
        const cols = {
            name: lh.idx(/^employee$/i), id: lh.idx(/^id$/i), cls: lh.idx(/^classification$/i), shift: lh.idx(/^shift$/i),
            pd: lh.idx(/^pd/i), tp: lh.idx(/^tp/i), rr: lh.idx(/^rr/i), dates: lh.dates,
        };
        const rows = [];
        for (let r = lh.r + 1; r <= lab.rowCount; r += 1) {
            // The Totals row counts people with a formula in the ID column — not a person.
            const idCell = lab.getCell(r, cols.id);
            const id = cellText(idCell.value).trim();
            const name = cellText(lab.getCell(r, cols.name).value).trim();
            if (/^\d+$/.test(id) && !(idCell.value && typeof idCell.value === 'object' && 'formula' in idCell.value) && !(idCell.value && idCell.value.sharedFormula) && !/^totals?$/i.test(name)) {
                rows.push({ id: String(Number(id)), name, r });
            }
        }
        out.labor = { sheet: lab.name, cols, rows };
    }
    const eq = wb.worksheets.find((w) => /^equipment$/i.test(w.name));
    const eh = eq && header(eq, (idx) => idx(/^equipment$/i) > 0);
    if (eh) {
        const cols = { desc: eh.idx(/^equipment$/i), unit: eh.idx(/^unit/i), type: eh.idx(/^type$/i), dates: eh.dates };
        const rows = [];
        for (let r = eh.r + 1; r <= eq.rowCount; r += 1) {
            const desc = cellText(eq.getCell(r, cols.desc).value).trim();
            if (!desc || /^totals?$/i.test(desc) || /^source:/i.test(desc) || /^week in progress/i.test(desc)) continue;
            const hrs = Object.values(cols.dates).reduce((a, c) => a + cellNum(eq.getCell(r, c).value), 0);
            if (!hrs) continue;
            rows.push({ desc, unit: cols.unit > 0 ? cellText(eq.getCell(r, cols.unit).value).trim() : '', r });
        }
        out.equip = { sheet: eq.name, cols, rows };
    }
    return out;
};

/**
 * A reviewed card workbook read in the browser (an imported file), in
 * readApprovedTimeCard's result shape plus the week it covers and the job
 * its title names: { found, weekEnd, job, dates, labor, equipment }.
 * The header dates carry no year; `today` decides it (a December card read
 * in January is last year's).
 */
export const parseCardWorkbook = (wb, today = moment().format('YYYY-MM-DD')) => {
    const t = moment(today, 'YYYY-MM-DD');
    // Read once with this year to learn the months, then settle the year.
    const probe = cardLayout(wb, `${t.year()}-01-01`);
    const anyDates = Object.keys((probe.labor || probe.equip || { cols: { dates: {} } }).cols.dates);
    if (!anyDates.length) return { found: false, reason: 'no Labor tab with dated columns' };
    const lastMonth = Math.max(...anyDates.map((d) => Number(d.slice(5, 7))));
    const year = lastMonth > t.month() + 2 ? t.year() - 1 : t.year();
    const layout = cardLayout(wb, `${year}-01-01`);
    const lab = wb.worksheets.find((w) => w.name === layout.labor?.sheet);
    const dates = Object.keys(layout.labor ? layout.labor.cols.dates : layout.equip.cols.dates).sort();
    const weekEnd = weekEndOf(dates[dates.length - 1]);
    const at = (ws, r, c) => (c > 0 ? ws.getCell(r, c).value : null);
    const labor = layout.labor ? layout.labor.rows.map(({ id, name, r }) => {
        const L = layout.labor.cols;
        const days = {};
        let total = 0;
        Object.entries(L.dates).forEach(([d, c]) => { const h = cellNum(at(lab, r, c)); if (h) { days[d] = round2(h); total += h; } });
        const shiftRaw = cellText(at(lab, r, L.shift)).trim();
        return {
            id, name, cls: cellText(at(lab, r, L.cls)).trim(), shiftRaw,
            pd: cellNum(at(lab, r, L.pd)), tp: cellNum(at(lab, r, L.tp)), rr: cellNum(at(lab, r, L.rr)), days, total: round2(total),
        };
    }) : [];
    const eqWs = layout.equip && wb.worksheets.find((w) => w.name === layout.equip.sheet);
    const equipment = layout.equip ? layout.equip.rows.map(({ desc, unit, r }) => {
        const E = layout.equip.cols;
        const days = {};
        let total = 0;
        Object.entries(E.dates).forEach(([d, c]) => { const h = cellNum(at(eqWs, r, c)); if (h) { days[d] = round2(h); total += h; } });
        return { desc, unit, type: cellText(at(eqWs, r, E.type)).trim(), days, total: round2(total) };
    }) : [];
    // "Job 54026071  —  VoltaGrid LLC  —  …" in the title rows.
    let job = '';
    (lab ? [lab] : []).forEach((ws) => {
        for (let r = 1; r <= 4 && !job; r += 1) {
            const mt = /\b(\d{8})\b/.exec(cellText(ws.getCell(r, 1).value));
            if (mt) job = mt[1];
        }
    });
    return { found: true, weekEnd, job, dates, labor, equipment };
};

/** Copy one tab across workbooks: values, formulas (sheet names mapped), styles, merges, widths, frozen panes. */
const copySheet = (src, dst, rename, VT) => {
    const ncol = Math.max(src.columnCount || 0, src.actualColumnCount || 0);
    for (let c = 1; c <= ncol; c += 1) {
        const sc = src.getColumn(c);
        if (sc.width) dst.getColumn(c).width = sc.width;
        if (sc.hidden) dst.getColumn(c).hidden = true;
    }
    src.eachRow({ includeEmpty: true }, (row, r) => {
        const out = dst.getRow(r);
        if (row.height) out.height = row.height;
        if (row.hidden) out.hidden = true;
        row.eachCell({ includeEmpty: true }, (cell, c) => {
            if (cell.type === VT.Merge) return;
            const d = out.getCell(c);
            const v = cell.value;
            if (cell.type === VT.Formula) d.value = { formula: rename(cell.formula), result: cell.result };
            else if (v instanceof Date || v == null || typeof v !== 'object') d.value = v;
            else d.value = JSON.parse(JSON.stringify(v));
            if (cell.style && Object.keys(cell.style).length) d.style = JSON.parse(JSON.stringify(cell.style));
        });
    });
    (src.model.merges || []).forEach((rg) => { try { dst.mergeCells(rg); } catch (e) { /* overlapping merge: leave it unmerged */ } });
    if (src.views && src.views.length) dst.views = src.views.map((v) => ({ ...v }));
    const ps = src.pageSetup || {};
    dst.pageSetup = { orientation: ps.orientation || 'landscape', fitToPage: ps.fitToPage, fitToWidth: ps.fitToWidth, fitToHeight: ps.fitToHeight };
    // A card saved in Excel keeps a rule that reads another tab (HJ Check's
    // COUNTIF(Labor!…) duplicate check) in its x14 extension; ExcelJS reads
    // that back with no formula and throws writing it ("reading '0'"), which
    // fails the whole download. Leave such rules out; the rest follow the renamed tabs.
    (src.conditionalFormattings || []).forEach((cf) => {
        const rules = (cf.rules || [])
            .filter((r) => !((r.type === 'expression' || r.type === 'cellIs') && !(r.formulae || []).length))
            .map((r) => (r.formulae ? { ...r, formulae: r.formulae.map((f) => (typeof f === 'string' ? rename(f) : f)) } : r));
        if (!rules.length) return;
        try {
            dst.addConditionalFormatting(JSON.parse(JSON.stringify({ ...cf, rules })));
        } catch (e) { /* a rule ExcelJS cannot write back: the copy keeps its values */ }
    });
};

/**
 * The workbook, formulas and all. opts.cardFiles: { [weekEnd]: the reviewed
 * card's .xlsx bytes } — each is copied in tab for tab ("TC 10.11.26 Labor"),
 * and the Time Card tabs' rows for that week read its cells.
 */
export const buildInvoiceWorkbook = async (model, opts = {}) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = model.preparedBy || 'Fusion';
    wb.created = new Date();
    wb.calcProperties.fullCalcOnLoad = true;
    const m = model;
    const sub = `Job ${m.job}${m.jobName ? ` — ${m.jobName}` : ''}${m.client ? ` — ${m.client}` : ''}   ·   ${m.weeks.map(weekLabel).join(', ')}`;
    const nL = m.labor.length; const nP = m.perDiem.length; const nE = m.equip.length;
    const nTL = m.tcLabor.length; const nTE = m.tcEquip.length; const nHL = m.hjLabor.length; const nHE = m.hjEquip.length;
    const sisterLabel = m.otherSource === 'payroll' ? 'other jobs' : m.sister ? `${m.sister}` : 'other job';

    // The reviewed cards, read before any tab is made so theirs sit in order.
    const embedNotes = [];
    const cards = [];
    const taken = new Set(Object.values(SH).map((n) => n.toLowerCase()));
    for (const we of m.weeks) {
        const bytes = (opts.cardFiles || {})[we];
        const info = m.weekInfo.find((w) => w.we === we);
        if (!bytes) {
            if (info && (info.laborBasis === 'card' || info.equipBasis === 'card')) embedNotes.push(`${weekLabel(we)}: the reviewed time card workbook could not be fetched, so its tabs are not in this file (the Time Card tabs carry its numbers as values).`);
            continue;
        }
        try {
            const src = new ExcelJS.Workbook();
            // eslint-disable-next-line no-await-in-loop
            await src.xlsx.load(bytes);
            const names = {};
            src.worksheets.forEach((w) => { names[w.name] = cardTabName(we, w.name, taken); });
            cards.push({ we, src, names, layout: cardLayout(src, we) });
        } catch (e) {
            embedNotes.push(`${weekLabel(we)}: the reviewed time card workbook could not be read (${e.message}), so its tabs are not in this file.`);
        }
    }

    // Sheets in reading order; the summary first, each week's card after the Time Card tabs.
    const ws = {};
    const ours = new Set(Object.values(SH));
    const order = [SH.sum, SH.li, SH.pd, SH.ei, SH.fuel, SH.tcl, SH.tce, ...cards.flatMap((c) => Object.values(c.names)), SH.hjl, SH.hje, SH.jc, SH.jde, SH.shifts];
    order.forEach((name) => {
        const frozen = name === SH.jc ? JC600_FIRST_ROW - 1 : HDR; // the JC600 tab's header is row 1, as JDE exports it
        ws[name] = wb.addWorksheet(name, { views: name === SH.sum || !ours.has(name) ? [] : [{ state: 'frozen', ySplit: frozen }], properties: { defaultRowHeight: 16 } });
    });
    cards.forEach((c) => c.src.worksheets.forEach((w) => copySheet(w, ws[c.names[w.name]], (f) => renameSheetRefs(f, c.names), ExcelJS.ValueType)));

    // ── Summary: inputs first, so every formula below can name them ──
    const s = ws[SH.sum];
    s.getColumn(1).width = 52;
    [2, 3, 4, 5, 6, 7, 8, 9].forEach((c) => { s.getColumn(c).width = 15; });
    s.getCell('A1').value = 'Labor & Equipment Invoice Backup';
    s.getCell('A1').font = { bold: true, size: 16, color: { argb: INK } };
    s.getCell('A2').value = `Job ${m.job}${m.jobName ? ` — ${m.jobName}` : ''}`;
    s.getCell('A2').font = { size: 12, color: { argb: INK } };
    s.getCell('A3').value = `${m.client ? `Client: ${m.client}   ·   ` : ''}Work ${m.range.from ? `${moment(m.range.from).format('M/D/YY')} – ${moment(m.range.to).format('M/D/YY')}` : '—'} (${m.weeks.map(weekLabel).join(', ')})`;
    s.getCell('A3').font = { color: { argb: GREY } };
    s.getCell('A4').value = `Prepared ${moment(m.preparedAt, 'YYYY-MM-DD HH:mm').format('M/D/YY h:mm A')}${m.preparedBy ? ` by ${m.preparedBy}` : ''}. Blue cells are inputs; every other number is a formula over the tabs behind it.`;
    s.getCell('A4').font = { italic: true, size: 10, color: { argb: MUTED } };

    const section = (row, title) => {
        const c = s.getCell(`A${row}`);
        c.value = title;
        c.font = { bold: true, size: 12, color: { argb: INK } };
        c.border = { bottom: { style: 'thin', color: { argb: GREY } } };
    };
    section(6, 'Rates');
    const inputs = [
        ['Labor multiplier (× JDE wage)', m.cfg.m, '0.000', 'Bill_Multiplier'],
        ['Overtime factor (× the straight rate)', m.cfg.otf, '0.00', 'OT_Factor'],
        ['Straight time per person per week, both jobs (hours)', m.rules.otAfter, '0.00', 'OT_After_Hours'],
        ['Per diem per day', m.rules.pdRate, RATE, 'PerDiem_Rate'],
        ['Hourly equipment: daily minimum (hours)', m.rules.hourlyMin, '0.00', 'Hourly_Min_Hours'],
        ['Daily equipment: half a day rate under (hours)', m.rules.halfDayUnder, '0.00', 'HalfDay_Under_Hours'],
    ];
    inputs.forEach(([label, v, fmt, name], i) => {
        const r = 7 + i;
        s.getCell(`A${r}`).value = label;
        const c = s.getCell(`B${r}`);
        c.value = v;
        c.numFmt = fmt;
        c.font = { color: { argb: NAVY } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TINT } };
        c.name = name;
    });

    // ── Labor Invoice ──
    const li = ws[SH.li];
    sheetHead(li, 'Labor Invoice', sub, `Hours from ${SH.tcl}; wage from ${SH.jde} (the JC600: base rate, hours-weighted, regular pay first, in the week billed, else the person's latest JC600 week — JC600 wage week); ST rate = ROUND(wage × multiplier, 2); OT rate = ROUND(wage × multiplier × OT factor, 2), a Project Director's OT at the ST rate.`);
    const liCols = [
        { h: 'Week ending', w: 11, fmt: DATE }, { h: 'ID', w: 9 }, { h: 'Employee', w: 24 }, { h: 'Classification', w: 28 }, { h: 'Shift', w: 7 }, { h: 'Basis', w: 11 },
        { h: 'Hours', w: 9, fmt: HOURS, right: true }, { h: `Hours on ${sisterLabel}`, w: 10, fmt: HOURS, right: true }, { h: `ST hrs on ${sisterLabel}`, w: 10, fmt: HOURS, right: true, input: true },
        { h: 'ST hours', w: 9, fmt: HOURS, right: true }, { h: 'OT hours', w: 9, fmt: HOURS, right: true }, { h: 'JC600 wage', w: 10, fmt: WAGE, right: true },
        { h: 'ST rate', w: 10, fmt: RATE, right: true }, { h: 'OT rate', w: 10, fmt: RATE, right: true }, { h: 'ST amount', w: 12, fmt: MONEY, right: true },
        { h: 'OT amount', w: 12, fmt: MONEY, right: true }, { h: 'Labor total', w: 13, fmt: MONEY, right: true }, { h: 'HeavyJob hours', w: 10, fmt: HOURS, right: true },
        { h: 'Card − HJ', w: 9, fmt: HOURS, right: true }, { h: 'Wage note', w: 40 }, { h: 'JC600 wage week', w: 10, fmt: 'm/d/yy;;"—"', right: true },
    ];
    tableHeader(li, HDR, liCols);
    // The JDE Labor Cost tab's columns (each JC600 line worked out), to the last row its formulas fill.
    const J = (col) => `${q(SH.jde)}!$${col}$${R0}:$${col}$${m.jcLast}`;
    const JC = { amt: 'C', hrs: 'D', id: 'I', pt: 'K', we: 'L', base: 'N' };
    const wageF = (r, t) => `ROUND(SUMIFS(${J(JC.base)},${J(JC.id)},$B${r},${J(JC.we)},$U${r},${J(JC.pt)},${t})/SUMIFS(${J(JC.hrs)},${J(JC.id)},$B${r},${J(JC.we)},$U${r},${J(JC.pt)},${t}),3)`;
    const TL = (col) => rng(SH.tcl, col, nTL);
    const HL = (col) => rng(SH.hjl, col, nHL);
    m.labor.forEach((l, i) => {
        const r = R0 + i;
        writeRow(li, r, liCols, [
            xDate(l.we), idCell(l.id), l.name, l.cls, l.shift, l.basis,
            fx(`SUMIFS(${TL('Q')},${TL('A')},$A${r},${TL('D')},$B${r})`, l.hrs),
            l.otherHrs, l.otherSt,
            fx(`MAX(0,MIN(G${r},OT_After_Hours-I${r}))`, l.st),
            fx(`G${r}-J${r}`, l.ot),
            fx(`IF($U${r}=0,"",IFERROR(${wageF(r, 1)},IFERROR(${wageF(r, 7)},IFERROR(${wageF(r, 5)},""))))`, l.wage == null ? '' : l.wage),
            fx(`IF(L${r}="","",ROUND(L${r}*Bill_Multiplier,2))`, l.stRate == null ? '' : l.stRate),
            fx(`IF(L${r}="","",IF(D${r}=${str(OT_STRAIGHT_CLASS)},M${r},ROUND(L${r}*Bill_Multiplier*OT_Factor,2)))`, l.otRate == null ? '' : l.otRate),
            fx(`IF(M${r}="",0,ROUND(J${r}*M${r},2))`, l.stAmt),
            fx(`IF(N${r}="",0,ROUND(K${r}*N${r},2))`, l.otAmt),
            fx(`O${r}+P${r}`, l.total),
            fx(`SUMIFS(${HL('L')},${HL('B')},$A${r},${HL('F')},$B${r})`, l.hjHrs),
            fx(`G${r}-R${r}`, l.diff),
            fx(`IF(L${r}="",${str(NO_WAGE_NOTE)},IF(U${r}=A${r},"JC600 WE "&TEXT(A${r},"m/d/yy"),"no JC600 payroll this week: WE "&TEXT(U${r},"m/d/yy")&" wage"))`, l.wageNote),
            // The week the wage comes from: this week when the person has JC600 hours in it, else their latest.
            fx(`IF(COUNTIFS(${J(JC.id)},$B${r},${J(JC.we)},$A${r},${J(JC.hrs)},"<>0")>0,$A${r},_xlfn.MAXIFS(${J(JC.we)},${J(JC.id)},$B${r},${J(JC.hrs)},"<>0"))`, l.wageWeek ? xDate(l.wageWeek) : 0),
        ]);
        if (l.basis === 'Time card' && Math.abs(l.diff) > 0.001) flagCell(li.getCell(r, 19), 'warn');
    });
    const liLast = R0 + Math.max(nL, 1) - 1;
    // Unpriced and carried wages light up by rule, so they follow a JC600 pasted in later.
    const tone = (t) => ({ fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: t === 'bad' ? BAD_TINT : WARN_TINT } }, ...(t === 'bad' ? { font: { color: { argb: BAD_TEXT } } } : {}) });
    const unpricedRule = { type: 'expression', priority: 1, formulae: [`$L${R0}=""`], style: tone('bad') };
    li.addConditionalFormatting({ ref: `L${R0}:L${liLast} T${R0}:T${liLast}`, rules: [unpricedRule] });
    li.addConditionalFormatting({ ref: `L${R0}:L${liLast} U${R0}:U${liLast}`, rules: [{ type: 'expression', priority: 2, formulae: [`AND($L${R0}<>"",$U${R0}<>$A${R0})`], style: tone('warn') }] });
    const liTot = R0 + nL;
    const colSum = (col, a = R0, b = liLast) => `SUM(${col}${a}:${col}${b})`;
    totalRow(li, liTot, liCols, ['Total', null, `${nL} person-week${nL === 1 ? '' : 's'}`, null, null, null,
        fx(colSum('G'), m.totals.hrs), null, null, fx(colSum('J'), m.totals.st), fx(colSum('K'), m.totals.ot), null, null, null,
        fx(colSum('O'), round2(m.labor.reduce((a, l) => a + l.stAmt, 0))), fx(colSum('P'), round2(m.labor.reduce((a, l) => a + l.otAmt, 0))), fx(colSum('Q'), m.totals.labor),
        fx(colSum('R'), round2(m.labor.reduce((a, l) => a + l.hjHrs, 0))), fx(colSum('S'), round2(m.labor.reduce((a, l) => a + l.diff, 0))), null, null]);
    li.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: liCols.length } };
    const LI = (col) => `${q(SH.li)}!$${col}$${R0}:$${col}$${liLast}`;

    // ── Per Diem ──
    const pd = ws[SH.pd];
    sheetHead(pd, 'Per Diem', sub, `Per diem days from ${SH.tcl} (the reviewed card bills) × the per diem rate. JC600 per diem paid (pay type 620 in the JC600) and HeavyJob days are shown to check against, not billed.`);
    const pdCols = [
        { h: 'Week ending', w: 11, fmt: DATE }, { h: 'ID', w: 9 }, { h: 'Employee', w: 24 }, { h: 'Classification', w: 28 },
        { h: 'PD days', w: 9, fmt: HOURS, right: true }, { h: 'Rate', w: 10, fmt: RATE, right: true }, { h: 'Per diem', w: 12, fmt: MONEY, right: true },
        { h: 'JC600 per diem paid', w: 12, fmt: MONEY, right: true }, { h: 'HeavyJob PD days', w: 10, fmt: HOURS, right: true }, { h: 'Card − HJ days', w: 10, fmt: HOURS, right: true },
    ];
    tableHeader(pd, HDR, pdCols);
    m.perDiem.forEach((p, i) => {
        const r = R0 + i;
        const row = writeRow(pd, r, pdCols, [
            xDate(p.we), idCell(p.id), p.name, p.cls,
            fx(`SUMIFS(${TL('G')},${TL('A')},$A${r},${TL('D')},$B${r})`, p.days),
            fx('PerDiem_Rate', p.rate),
            fx(`ROUND(E${r}*F${r},2)`, p.amt),
            fx(`SUMIFS(${J(JC.amt)},${J(JC.id)},$B${r},${J(JC.we)},$A${r},${J(JC.pt)},${PER_DIEM_PT})`, p.jdePaid),
            fx(`COUNTIFS(${HL('B')},$A${r},${HL('F')},$B${r},${HL('K')},"Per Diem")`, p.hjDays),
            fx(`E${r}-I${r}`, p.diff),
        ]);
        if (p.diff) flagCell(row.getCell(10), 'warn');
    });
    const pdLast = R0 + Math.max(nP, 1) - 1;
    totalRow(pd, R0 + nP, pdCols, ['Total', null, `${nP} person-week${nP === 1 ? '' : 's'}`, null,
        fx(`SUM(E${R0}:E${pdLast})`, m.totals.pdDays), null, fx(`SUM(G${R0}:G${pdLast})`, m.totals.pd),
        fx(`SUM(H${R0}:H${pdLast})`, round2(m.perDiem.reduce((a, p) => a + p.jdePaid, 0))), fx(`SUM(I${R0}:I${pdLast})`, m.perDiem.reduce((a, p) => a + p.hjDays, 0)), null]);
    pd.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: pdCols.length } };
    const PD = (col) => `${q(SH.pd)}!$${col}$${R0}:$${col}$${pdLast}`;

    // ── Shifts (read by the daily equipment formula) ──
    const shw = ws[SH.shifts];
    sheetHead(shw, 'Shifts by day', sub, `Shifts that worked each day, off the Shift column of ${SH.tcl}: 1 = days only (or nights only), 2 = days and nights. A daily unit bills one day rate per shift.`);
    const shCols = [{ h: 'Week ending', w: 12, fmt: DATE }, ...DOW.map((d) => ({ h: d, w: 8, right: true, fmt: '0' }))];
    tableHeader(shw, HDR, shCols);
    m.shifts.forEach((x, i) => {
        const r = R0 + i;
        const dayCol = (k) => `${q(SH.tcl)}!${colL(10 + k)}$${R0}:${colL(10 + k)}$${R0 + Math.max(nTL, 1) - 1}`;
        const cnt = (k, shift) => `COUNTIFS(${TL('A')},$A${r},${TL('F')},${str(shift)},${dayCol(k)},">0")`;
        writeRow(shw, r, shCols, [xDate(x.we), ...x.s.map((v, k) => fx(`(${cnt(k, 'D')}+${cnt(k, 'D+N')}>0)+(${cnt(k, 'N')}+${cnt(k, 'D+N')}>0)`, v))]);
    });
    const shLast = R0 + Math.max(m.shifts.length, 1) - 1;
    const SHR = `${q(SH.shifts)}!$B$${R0}:$H$${shLast}`;
    const SHA = `${q(SH.shifts)}!$A$${R0}:$A$${shLast}`;

    // ── Fuel (rates read by the Equipment Invoice) ──
    const fw = ws[SH.fuel];
    sheetHead(fw, 'Fuel', sub, 'Equipment fuel surcharge: the US Gulf Coast (PADD 3) on-highway diesel average of the quarter BEFORE the work, through the rate sheet\'s index. Fuel-burning equipment only; not labor, per diem or materials.');
    const fIn = [
        ['Index threshold ($/gal)', m.fuelIdx.threshold, '$0.00', 'Fuel_Threshold'],
        ['Index step ($/gal)', m.fuelIdx.step, '$0.00', 'Fuel_Step'],
        ['Adder per step', m.fuelIdx.inc, '0.00"%"', 'Fuel_Step_Pct'],
    ];
    fIn.forEach(([label, v, fmt, name], i) => {
        const r = 5 + i;
        fw.getCell(`A${r}`).value = label;
        const c = fw.getCell(`B${r}`);
        c.value = v; c.numFmt = fmt; c.name = name;
        c.font = { color: { argb: NAVY } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TINT } };
    });
    const fqHdr = 9;
    const fqCols = [{ h: 'Price quarter' }, { h: 'Applies to work in' }, { h: 'Gulf Coast diesel avg $/gal', fmt: '$0.0000', right: true, input: true }, { h: 'Adder', fmt: PCT, right: true }];
    tableHeader(fw, fqHdr, fqCols);
    m.fuelQ.forEach((f, i) => {
        const r = fqHdr + 1 + i;
        const row = writeRow(fw, r, fqCols, [f.q, f.applies, f.price == null ? null : f.price,
            fx(`IF(C${r}="",0,IF(C${r}<Fuel_Threshold,0,ROUND((C${r}-Fuel_Threshold)/Fuel_Step*Fuel_Step_Pct,2)/100))`, f.pct)]);
        if (f.missing) flagCell(row.getCell(3), 'warn');
    });
    const fqFirst = fqHdr + 1;
    const fqLast = fqHdr + Math.max(m.fuelQ.length, 1);
    wb.definedNames.add(`Fuel!$A$${fqFirst}:$D$${fqLast}`, 'Fuel_Rates');
    // The rest of the tab, top to bottom: what the adder applies to, by
    // quarter, by week, then one row per Equipment Invoice row — where each
    // unit's surcharge is worked out (the Equipment Invoice reads it back).
    const FL = { applies: fqLast + 3 };
    FL.byQ = FL.applies + 7;
    FL.byW = FL.byQ + Math.max(m.fuelQ.length, 1) + 3;
    FL.calc = FL.byW + Math.max(m.byWeek.length, 1) + 3;
    FL.c0 = FL.calc + 1;
    FL.cLast = FL.calc + Math.max(nE, 1);
    FL.buys = FL.calc + Math.max(nE, 1) + 4;
    const FC = (col) => `${q(SH.fuel)}!$${col}$${FL.c0}:$${col}$${FL.cLast}`;

    // ── Equipment Invoice ──
    const ei = ws[SH.ei];
    sheetHead(ei, 'Equipment Invoice', sub, `Hours from ${SH.tce}. Hourly: each day's hours, a daily minimum. Daily: one day rate per shift that worked (${SH.shifts}), half a rate under the half-day hours. The fuel adder and surcharge come from this unit's row on the ${SH.fuel} tab.`);
    const eiCols = [
        { h: 'Week ending', w: 11, fmt: DATE }, { h: 'Equipment', w: 30 }, { h: 'Unit No', w: 13 }, { h: 'Type', w: 11 }, { h: 'Rate sheet item', w: 34 },
        { h: 'UOM', w: 6 }, { h: 'Rate', w: 10, fmt: RATE, right: true }, { h: 'Burns fuel', w: 7 },
        ...DOW.map((d) => ({ h: `${d} hrs`, w: 8.5, fmt: HOURS, right: true })),
        { h: 'Card hours', w: 9, fmt: HOURS, right: true }, { h: 'Days', w: 6, fmt: '0', right: true }, { h: 'Billed qty', w: 9, fmt: HOURS, right: true },
        { h: 'Amount', w: 12, fmt: MONEY, right: true }, { h: 'Fuel quarter', w: 10 }, { h: 'Fuel adder', w: 9, fmt: PCT, right: true },
        { h: 'Fuel surcharge', w: 11, fmt: MONEY, right: true }, { h: 'Total', w: 12, fmt: MONEY, right: true },
        { h: 'HeavyJob hours', w: 10, fmt: HOURS, right: true }, { h: 'Card − HJ', w: 9, fmt: HOURS, right: true }, { h: 'Note', w: 44 },
    ];
    tableHeader(ei, HDR, eiCols);
    const HE = (col) => rng(SH.hje, col, nHE);
    m.equip.forEach((e, i) => {
        const r = R0 + i;
        const days = DOW.map((_, k) => {
            if (!e.part.includes(k)) return null;
            const refs = e.refs.map((ri) => `${q(SH.tce)}!${colL(6 + k)}${R0 + ri}`);
            return fx(refs.join('+'), e.days7[k]);
        });
        const D = `I${r}:O${r}`;
        const S = `INDEX(${SHR},MATCH($A${r},${SHA},0),0)`;
        const qtyF = `IF(F${r}="",0,IF(F${r}="hr",SUMPRODUCT((${D}>0)*((${D}<Hourly_Min_Hours)*Hourly_Min_Hours+(${D}>=Hourly_Min_Hours)*${D})),`
            + `IF(F${r}="day",SUMPRODUCT((${D}>=HalfDay_Under_Hours)*(${S}+(${S}=0)))+0.5*COUNTIFS(${D},">0",${D},"<"&HalfDay_Under_Hours),COUNTIF(${D},">0"))))`;
        const row = writeRow(ei, r, eiCols, [
            xDate(e.we), e.desc, e.unit, e.type, e.item, e.uom, e.rate == null ? null : e.rate, e.item ? (e.fu ? 'Y' : 'N') : '',
            ...days,
            fx(`SUM(${D})`, e.cardHrs), fx(`COUNTIF(${D},">0")`, e.n),
            fx(qtyF, e.qty),
            fx(`IF(G${r}="",0,ROUND(R${r}*G${r},2))`, e.amt),
            e.fq,
            fx(`${q(SH.fuel)}!K${FL.c0 + i}`, e.fpct),
            fx(`${q(SH.fuel)}!L${FL.c0 + i}`, e.fuel),
            fx(`S${r}+V${r}`, e.total),
            e.hjHrs == null ? null : fx(`SUMIFS(${HE('K')},${HE('B')},$A${r},${HE('F')},$C${r})`, e.hjHrs),
            e.hjHrs == null ? null : fx(e.split ? `SUMIFS(${rng(SH.tce, 'M', nTE)},${rng(SH.tce, 'A', nTE)},$A${r},${rng(SH.tce, 'D', nTE)},$C${r})-X${r}` : `P${r}-X${r}`, e.split
                ? round2(m.tcEquip.filter((t) => t.we === e.we && t.unit === e.unit).reduce((a, t) => a + t.total, 0) - e.hjHrs)
                : round2(e.cardHrs - e.hjHrs)),
            [e.split ? 'week crosses a fuel quarter — one row per quarter' : '', e.note].filter(Boolean).join('; '),
        ]);
        if (!e.item) { flagCell(row.getCell(5), 'bad'); flagCell(row.getCell(26), 'bad'); }
    });
    const eiLast = R0 + Math.max(nE, 1) - 1;
    totalRow(ei, R0 + nE, eiCols, ['Total', `${nE} row${nE === 1 ? '' : 's'}`, null, null, null, null, null, null, null, null, null, null, null, null, null,
        fx(`SUM(P${R0}:P${eiLast})`, round2(m.equip.reduce((a, e) => a + e.cardHrs, 0))), null, null,
        fx(`SUM(S${R0}:S${eiLast})`, m.totals.equip), null, null, fx(`SUM(V${R0}:V${eiLast})`, m.totals.fuel), fx(`SUM(W${R0}:W${eiLast})`, m.totals.equipTotal), null, null, null]);
    ei.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: eiCols.length } };
    const EI = (col) => `${q(SH.ei)}!$${col}$${R0}:$${col}$${eiLast}`;

    // Fuel, continued: what the adder applies to, by quarter, by week, each
    // unit's surcharge, then the fuel the job bought.
    const fsec = (row, title, note) => {
        fw.getCell(`A${row}`).value = title;
        fw.getCell(`A${row}`).font = { bold: true, size: 12, color: { argb: INK } };
        if (note) {
            fw.getCell(`D${row}`).value = note;
            fw.getCell(`D${row}`).font = { italic: true, size: 10, color: { argb: MUTED } };
        }
    };
    const burns = (e) => e.item && e.fu;
    const fuelBase = round2(m.equip.filter(burns).reduce((a, e) => a + e.amt, 0));
    const noFuel = round2(m.equip.filter((e) => e.item && !e.fu).reduce((a, e) => a + e.amt, 0));
    fsec(FL.applies, 'What the surcharge applies to', 'Equipment only, and only machines with an engine of their own; not labor, per diem or materials.');
    [
        ['Equipment billed at the rate sheet', fx(`SUM(${EI('S')})`, m.totals.equip), MONEY],
        ['less equipment that burns no fuel (trailers, attachments, hand tools)', fx(`SUMIFS(${EI('S')},${EI('H')},"N")`, noFuel), MONEY],
        ['Fuel-burning equipment: the surcharge base', fx(`B${FL.applies + 1}-B${FL.applies + 2}`, fuelBase), MONEY],
        ['Fuel surcharge (sum of the equipment rows below)', fx(`SUM(${FC('L')})`, m.totals.fuel), MONEY],
        ['Effective adder', fx(`IF(B${FL.applies + 3}=0,0,B${FL.applies + 4}/B${FL.applies + 3})`, fuelBase ? m.totals.fuel / fuelBase : 0), PCT],
    ].forEach(([label, v, fmt], i) => {
        const r = FL.applies + 1 + i;
        fw.getCell(`A${r}`).value = label;
        const c = fw.getCell(`B${r}`);
        c.value = v;
        c.numFmt = fmt;
        c.border = { bottom: { style: 'thin', color: { argb: LINE } } };
        if (i >= 2) { fw.getCell(`A${r}`).font = { bold: true }; c.font = { bold: true }; }
    });

    fsec(FL.byQ - 1, 'Surcharge by fuel quarter');
    const fbqCols = [{ h: 'Price quarter' }, { h: 'Applies to work in' }, { h: 'Fuel-burning equipment', fmt: MONEY, right: true }, { h: 'Adder', fmt: PCT, right: true }, { h: 'Fuel surcharge', fmt: MONEY, right: true }];
    tableHeader(fw, FL.byQ, fbqCols);
    m.fuelQ.forEach((f, i) => {
        const r = FL.byQ + 1 + i;
        const inQ = m.equip.filter((e) => e.fq === f.q);
        writeRow(fw, r, fbqCols, [f.q, f.applies,
            fx(`SUMIFS(${FC('H')},${FC('I')},$A${r},${FC('E')},"Y")`, round2(inQ.filter(burns).reduce((a, e) => a + e.amt, 0))),
            fx(`IFERROR(VLOOKUP($A${r},Fuel_Rates,4,FALSE),0)`, f.pct),
            fx(`SUMIFS(${FC('L')},${FC('I')},$A${r})`, round2(inQ.reduce((a, e) => a + e.fuel, 0)))]);
    });
    if (!m.fuelQ.length) fw.getCell(`A${FL.byQ + 1}`).value = 'No equipment in these weeks.';

    fsec(FL.byW - 1, 'Surcharge by week');
    const fwCols = [{ h: 'Week ending', fmt: DATE }, { h: 'Fuel-burning equipment', fmt: MONEY, right: true }, { h: 'Fuel surcharge', fmt: MONEY, right: true }, { h: 'Effective adder', fmt: PCT, right: true }];
    tableHeader(fw, FL.byW, fwCols);
    m.byWeek.forEach((w, i) => {
        const r = FL.byW + 1 + i;
        const base = round2(m.equip.filter((e) => e.we === w.we && burns(e)).reduce((a, e) => a + e.amt, 0));
        writeRow(fw, r, fwCols, [xDate(w.we),
            fx(`SUMIFS(${FC('H')},${FC('A')},$A${r},${FC('E')},"Y")`, base),
            fx(`SUMIFS(${FC('L')},${FC('A')},$A${r})`, w.fuel),
            fx(`IF(B${r}=0,0,C${r}/B${r})`, base ? w.fuel / base : 0)]);
        fw.getCell(`A${r}`).alignment = { horizontal: 'left' };
    });

    fsec(FL.calc - 1, 'Surcharge by equipment', `One row per ${SH.ei} row: its amount × the adder of the quarter before the work. ${SH.ei} reads its Fuel adder and Fuel surcharge from here.`);
    const fcCols = [
        { h: 'Week ending', fmt: DATE }, { h: 'Equipment' }, { h: 'Unit No' }, { h: 'Rate sheet item' }, { h: 'Burns fuel' },
        { h: 'Billed qty', fmt: HOURS, right: true }, { h: 'Rate', fmt: RATE, right: true }, { h: 'Equipment amount', fmt: MONEY, right: true },
        { h: 'Fuel quarter' }, { h: 'Diesel avg $/gal', fmt: '$0.0000;-$0.0000;"—"', right: true }, { h: 'Adder', fmt: PCT, right: true }, { h: 'Fuel surcharge', fmt: MONEY, right: true },
    ];
    tableHeader(fw, FL.calc, fcCols);
    const eiRef = (col, i) => `${q(SH.ei)}!${col}${R0 + i}`;
    m.equip.forEach((e, i) => {
        const r = FL.c0 + i;
        const fq = m.fuelQ.find((f) => f.q === e.fq);
        writeRow(fw, r, fcCols, [xDate(e.we), e.desc, e.unit, e.item || '(no rate sheet item)',
            fx(eiRef('H', i), e.item ? (e.fu ? 'Y' : 'N') : ''),
            fx(eiRef('R', i), e.qty),
            fx(eiRef('G', i), e.rate == null ? 0 : e.rate),
            fx(eiRef('S', i), e.amt),
            fx(eiRef('T', i), e.fq),
            fx(`IFERROR(VLOOKUP(I${r},Fuel_Rates,3,FALSE),"")`, fq && fq.price != null ? fq.price : 0),
            fx(`IF(E${r}="Y",IFERROR(VLOOKUP(I${r},Fuel_Rates,4,FALSE),0),0)`, e.fpct),
            fx(`ROUND(H${r}*K${r},2)`, e.fuel)]);
        fw.getCell(`A${r}`).alignment = { horizontal: 'left' };
    });
    totalRow(fw, FL.calc + nE + 1, fcCols, ['Total', `${nE} row${nE === 1 ? '' : 's'}`, null, null, null, null, null,
        fx(`SUM(H${FL.c0}:H${FL.cLast})`, m.totals.equip), null, null, null, fx(`SUM(L${FL.c0}:L${FL.cLast})`, m.totals.fuel)]);

    fsec(FL.buys - 1, 'Fuel bought on the job (JDE cost type 52300)', 'Billed from cost history with third-party costs, kept out of the surcharge base.');
    const ftCols = [{ h: 'Work date', fmt: DATE }, { h: 'Vendor' }, { h: 'PO' }, { h: 'Line' }, { h: 'Description' }, { h: 'Cost code' }, { h: 'Cost', fmt: MONEY, right: true }, { h: 'Billable' }, { h: 'Bill amount', fmt: MONEY, right: true }, { h: 'On summary' }];
    tableHeader(fw, FL.buys, ftCols);
    m.fuelTx.forEach((t, i) => {
        writeRow(fw, FL.buys + 1 + i, ftCols, [xDate(t.date), t.vendor, t.po, t.line, t.desc, t.cc, t.cost, t.billable, t.bill, t.inv]);
    });
    if (!m.fuelTx.length) fw.getCell(`A${FL.buys + 1}`).value = 'No 52300 fuel cost lines dated in these weeks.';
    else {
        const a = FL.buys + 1; const b = FL.buys + m.fuelTx.length;
        totalRow(fw, b + 1, ftCols, ['Total', null, null, null, null, null, fx(`SUM(G${a}:G${b})`, round2(m.fuelTx.reduce((x, t) => x + t.cost, 0))), null, fx(`SUM(I${a}:I${b})`, round2(m.fuelTx.reduce((x, t) => x + t.bill, 0))), null]);
    }
    [44, 34, 14, 34, 10, 10, 10, 14, 11, 12, 10, 13].forEach((w, i) => { fw.getColumn(i + 1).width = w; });

    // ── Time Card - Labor ──
    const tl = ws[SH.tcl];
    sheetHead(tl, 'Time Card — Labor', sub, 'The reviewed weekly time card (Labor tab) — the final source for hours, shift, classification and per diem. A reviewed week\'s name, per diem and day cells point at its own "TC" tab in this file. Day columns are the seven days of the row\'s week, Monday first.');
    const tlCols = [
        { h: 'Week ending', w: 11, fmt: DATE }, { h: 'Basis', w: 30 }, { h: 'Employee', w: 24 }, { h: 'ID', w: 9 }, { h: 'Classification', w: 28 }, { h: 'Shift', w: 6 },
        { h: 'PD days', w: 6, fmt: 'General', right: true }, { h: 'TP days', w: 6, fmt: 'General', right: true }, { h: 'RR days', w: 6, fmt: 'General', right: true },
        ...DOW.map((d) => ({ h: d, w: 8.5, fmt: HOURS, right: true })), { h: 'Total hours', w: 9, fmt: HOURS, right: true },
    ];
    tableHeader(tl, HDR, tlCols);
    // A reviewed week whose card is in this file reads that card's cells, so an
    // edit on the card tab flows through to the invoice.
    const cardOfWeek = Object.fromEntries(cards.map((c) => [c.we, c]));
    const fromCard = (basis) => /^Reviewed/.test(basis || '');
    const seenL = {};
    m.tcLabor.forEach((p, i) => {
        const r = R0 + i;
        const c = cardOfWeek[p.we];
        const L = c && c.layout.labor;
        let at = null;
        if (L && fromCard(p.basis)) {
            const key = `${p.id}|${norm(p.name)}`;
            const seenKey = `${p.we}|${key}`;
            seenL[seenKey] = (seenL[seenKey] || 0) + 1;
            const k = seenL[seenKey];
            at = L.rows.filter((x) => `${x.id}|${norm(x.name)}` === key)[k - 1] || null;
        }
        if (!at) {
            writeRow(tl, r, tlCols, [xDate(p.we), p.basis, p.name, idCell(p.id), p.cls, p.shift, p.pd || null, p.tp || null, p.rr || null,
                ...p.days7.map((h) => (h ? h : null)), fx(`SUM(J${r}:P${r})`, p.total)]);
            return;
        }
        const tab = `${q(c.names[L.sheet])}!`;
        const ref = (col, v) => (col > 0 ? fx(`${tab}${colL(col)}${at.r}`, v) : (v || null));
        const dates = weekDates(p.we);
        writeRow(tl, r, tlCols, [xDate(p.we), p.basis, ref(L.cols.name, p.name), idCell(p.id), p.cls, p.shift,
            ref(L.cols.pd, p.pd), ref(L.cols.tp, p.tp), ref(L.cols.rr, p.rr),
            ...dates.map((d, k) => (L.cols.dates[d] ? fx(`${tab}${colL(L.cols.dates[d])}${at.r}`, p.days7[k]) : null)),
            fx(`SUM(J${r}:P${r})`, p.total)]);
    });
    tl.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: tlCols.length } };

    // ── Time Card - Equipment ──
    const te = ws[SH.tce];
    sheetHead(te, 'Time Card — Equipment', sub, 'The reviewed weekly time card (Equipment tab), else the HeavyJob cards for a week with no reviewed card. A reviewed week\'s equipment and day cells point at its own "TC" tab in this file. Day columns are the seven days of the row\'s week, Monday first.');
    const teCols = [
        { h: 'Week ending', w: 11, fmt: DATE }, { h: 'Basis', w: 30 }, { h: 'Equipment', w: 34 }, { h: 'Unit No', w: 13 }, { h: 'Type', w: 12 },
        ...DOW.map((d) => ({ h: d, w: 8.5, fmt: HOURS, right: true })), { h: 'Total hours', w: 10, fmt: HOURS, right: true }, { h: 'Days used', w: 7, fmt: '0', right: true },
    ];
    tableHeader(te, HDR, teCols);
    const seenE = {};
    m.tcEquip.forEach((u, i) => {
        const r = R0 + i;
        const c = cardOfWeek[u.we];
        const E = c && c.layout.equip;
        let at = null;
        if (E && fromCard(u.basis)) {
            const key = `${u.desc}|${u.unit}`;
            const seenKey = `${u.we}|${key}`;
            seenE[seenKey] = (seenE[seenKey] || 0) + 1;
            const k = seenE[seenKey];
            at = E.rows.filter((x) => `${x.desc}|${x.unit}` === key)[k - 1] || null;
        }
        const tab = at ? `${q(c.names[E.sheet])}!` : '';
        const dates = weekDates(u.we);
        writeRow(te, r, teCols, [xDate(u.we), u.basis, at ? fx(`${tab}${colL(E.cols.desc)}${at.r}`, u.desc) : u.desc, u.unit, u.type,
            ...dates.map((d, k) => (at && E.cols.dates[d] ? fx(`${tab}${colL(E.cols.dates[d])}${at.r}`, u.days7[k]) : (u.days7[k] ? u.days7[k] : null))),
            fx(`SUM(F${r}:L${r})`, u.total), fx(`COUNTIF(F${r}:L${r},">0")`, u.n)]);
    });
    te.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: teCols.length } };

    // ── HJ Labor ──
    const hl = ws[SH.hjl];
    sheetHead(hl, 'HeavyJob — Labor', sub, 'Every labor line on the HeavyJob cards (live), card × person × cost code. Per Diem / Truck Pay / Rig Pay rows are the card adders (one per person-day).');
    const hlCols = [
        { h: 'Work date', w: 10, fmt: DATE }, { h: 'Week ending', w: 11, fmt: DATE }, { h: 'Card', w: 12 }, { h: 'Foreman', w: 22 }, { h: 'Shift', w: 7 },
        { h: 'ID', w: 9 }, { h: 'Employee', w: 24 }, { h: 'Pay class', w: 26 }, { h: 'Cost code', w: 10 }, { h: 'Cost code description', w: 28 }, { h: 'Type', w: 10 }, { h: 'Hours', w: 11, fmt: HOURS, right: true },
    ];
    tableHeader(hl, HDR, hlCols);
    m.hjLabor.slice().sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name) || a.cc.localeCompare(b.cc)).forEach((x, i) => {
        writeRow(hl, R0 + i, hlCols, [xDate(x.date), xDate(x.we), x.card, x.foreman, x.shift, idCell(x.id), x.name, x.payClass, x.cc, x.ccd, x.type, x.hrs]);
    });
    if (nHL) totalRow(hl, R0 + nHL, hlCols, ['Total', null, null, null, null, null, null, null, null, null, null, fx(`SUM(L${R0}:L${R0 + nHL - 1})`, round2(m.hjLabor.reduce((a, x) => a + x.hrs, 0)))]);
    hl.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: hlCols.length } };

    // ── HJ Equipment ──
    const he = ws[SH.hje];
    sheetHead(he, 'HeavyJob — Equipment', sub, 'Every equipment line on the HeavyJob cards (live), card × unit × cost code. Rentals are the PO-<po>-<line> units.');
    const heCols = [
        { h: 'Work date', w: 10, fmt: DATE }, { h: 'Week ending', w: 11, fmt: DATE }, { h: 'Card', w: 12 }, { h: 'Foreman', w: 22 }, { h: 'Shift', w: 7 },
        { h: 'Unit', w: 14 }, { h: 'Description', w: 36 }, { h: 'Rental', w: 7 }, { h: 'Cost code', w: 10 }, { h: 'Cost code description', w: 28 }, { h: 'Hours', w: 11, fmt: HOURS, right: true },
    ];
    tableHeader(he, HDR, heCols);
    m.hjEquip.slice().sort((a, b) => a.date.localeCompare(b.date) || a.unit.localeCompare(b.unit)).forEach((x, i) => {
        writeRow(he, R0 + i, heCols, [xDate(x.date), xDate(x.we), x.card, x.foreman, x.shift, x.unit, x.desc, x.rental, x.cc, x.ccd, x.hrs]);
    });
    if (nHE) totalRow(he, R0 + nHE, heCols, ['Total', null, null, null, null, null, null, null, null, null, fx(`SUM(K${R0}:K${R0 + nHE - 1})`, round2(m.hjEquip.reduce((a, x) => a + x.hrs, 0)))]);
    he.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: heCols.length } };

    // ── JC600: the export exactly as JDE writes it, A:W with the header in
    // row 1 and nothing else on the tab, so the export's own sheet pastes in
    // whole at A1 (select all, copy, paste). Read from JDE or imported on
    // the page, it is written here the same way. ──
    const jx = ws[SH.jc];
    const jc = m.jc600;
    const JL = m.jcLast;
    const JX0 = JC600_FIRST_ROW;
    const jt = jc ? jc.totals : { lines: 0, hrs: 0, amt: 0, baseAmt: 0, latest: null };
    const pasteW = [10, 10, 9, 24, 10, 12, 9, 24, 26, 9, 26, 12, 9, 11, 10, 9, 7, 11, 10, 10, 9, 10, 10];
    const pasteFmt = { 5: DATE, 6: MONEY, 7: HOURS, 19: DATE };
    pasteW.forEach((w, i) => {
        jx.getColumn(i + 1).width = w;
        if (pasteFmt[i + 1]) jx.getColumn(i + 1).numFmt = pasteFmt[i + 1];
    });
    const jxh = jx.getRow(JX0 - 1);
    jxh.height = 44;
    ((jc && jc.header) || JC600_HEADERS).forEach((h, i) => {
        const c = jxh.getCell(i + 1);
        c.value = h;
        c.font = { bold: true, size: 10, color: { argb: NAVY } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TINT } };
        c.alignment = { vertical: 'middle', wrapText: true };
    });
    jx.getCell(`A${JX0 - 1}`).note = `Paste the JC600 (Job Transaction Detail) export here: on the export's sheet press Ctrl+A, copy, then click A1 of this tab and paste. `
        + `JDE's header stays in row ${JX0 - 1} and the lines run from row ${JX0}, columns A:W; nothing else goes on this tab. ${SH.jde} reads it line by line.`;
    (jc ? jc.rows : []).forEach((vals, i) => {
        vals.forEach((v, c) => { if (v != null && v !== '') jx.getCell(JX0 + i, c + 1).value = v; });
    });
    jx.autoFilter = { from: { row: JX0 - 1, column: 1 }, to: { row: JX0 - 1, column: JC600_HEADERS.length } };

    // ── JDE Labor Cost: each JC600 line worked out by formula (to row jcLast),
    // the payroll lines' totals in row 4. Row r reads JC600 row r − D. ──
    // (No formula here is typed per row: Excel will not open a file whose
    // shared formulas name another sheet, so the echo columns are array
    // formulas and the rest read those.)
    const jw = ws[SH.jde];
    const D = R0 - JX0;
    const X = q(SH.jc);
    sheetHead(jw, 'JDE Labor Cost — what the JC600 says',
        `Job ${m.job}${m.jobName ? ` — ${m.jobName}` : ''}   ·   ${m.range.from ? `Work ${moment(m.range.from).format('M/D/YY')} – ${moment(m.range.to).format('M/D/YY')} (${m.weeks.map(weekLabel).join(', ')})` : 'no weeks'}`,
        `Formulas over the ${SH.jc} tab (JDE's Job Transaction Detail export for the job, pasted there at A1), one row per line of it. Payroll lines: doc type T2 with an "id - name" address number `
        + '(not the burden lines: address 0, Payroll Labor Distribution, cost type 51300). Pay type: the 3 digits before the last one in REMARK '
        + '(1 Regular, 5 Overtime 1.5, 7 STOver40, 600 Truck, 610 Rig, 620 Per DiemNT). Week ending: the Sunday on or after the GL date. '
        + `Base rate: amount ÷ units, ÷ 1.5 on overtime. The formulas run to row ${JL.toLocaleString('en-US')} (${SH.jc} row ${(JL - D).toLocaleString('en-US')}).${jc && jc.name ? `  ${SH.jc} loaded from ${jc.name}.` : `  ${SH.jc} is empty: paste the export there.`}`);
    // A:G echo the JC600's columns, one array formula each over the whole
    // tab (a shared formula that names another sheet is a file Excel will not
    // open); H:N work each line out from those, as shared formulas. A blank
    // JC600 row stays blank here.
    const jcRows = (col) => `${X}!$${col}$${JX0}:$${col}$${JL - D}`;
    const echo = (col) => `IF(${jcRows('A')}="","",${jcRows(col)})`;
    const echoNum = (col) => `IF(${jcRows('A')}="","",IF(ISNUMBER(${jcRows(col)}),${jcRows(col)},0))`;
    const raw = (i) => (l) => l.raw[i] ?? '';
    const rawNum = (i) => (l) => (typeof l.raw[i] === 'number' ? l.raw[i] : 0);
    const ifPay = (l, v, blank = '') => (l && l.payroll ? v : blank);
    const readers = [
        { h: 'Job', w: 10, array: echo('A'), v: raw(0) },
        { h: 'GL date', w: 10, fmt: DATE, array: echo('E'), v: raw(4) },
        { h: 'Amount', w: 12, fmt: MONEY, array: echoNum('F'), v: rawNum(5) },
        { h: 'Hours', w: 9, fmt: HOURS, array: echoNum('G'), v: rawNum(6) },
        { h: 'Remark', w: 26, array: echo('I'), v: raw(8) },
        { h: 'Address number', w: 26, array: echo('K'), v: raw(10) },
        { h: 'Doc type', w: 7, array: echo('M'), v: raw(12) },
        { h: 'Payroll?', w: 9, f: (r) => `AND($G${r}="T2",ISNUMBER(SEARCH(" - ",$F${r})))`, v: (l) => Boolean(l.payroll) },
        { h: 'ID', w: 9, f: (r) => `IF($H${r},IFERROR(--LEFT($F${r},FIND(" - ",$F${r})-1),""),"")`, v: (l) => ifPay(l, l.id ?? '') },
        { h: 'Employee', w: 24, f: (r) => `IF($H${r},TRIM(MID($F${r},FIND(" - ",$F${r})+3,99)),"")`, v: (l) => ifPay(l, l.name) },
        { h: 'Pay type', w: 8, f: (r) => `IF($H${r},IFERROR(--MID(TRIM($E${r}),LEN(TRIM($E${r}))-4,3),""),"")`, v: (l) => ifPay(l, l.pt ?? '') },
        { h: 'Week ending', w: 11, fmt: DATE, f: (r) => `IF($H${r},$B${r}+MOD(7-WEEKDAY($B${r},2),7),"")`, v: (l) => ifPay(l, l.we ? xDate(l.we) : '') },
        { h: 'Base rate', w: 10, fmt: WAGE, f: (r) => `IF(AND($H${r},$D${r}<>0),$C${r}/$D${r}/IF($K${r}=5,1.5,1),"")`, v: (l) => ifPay(l, l.base ?? '') },
        { h: 'Base × hours', w: 12, fmt: MONEY, f: (r) => `IF(AND($H${r},$D${r}<>0),$C${r}/IF($K${r}=5,1.5,1),0)`, v: (l) => ifPay(l, l.baseAmt, 0) },
    ];
    tableHeader(jw, HDR, readers);
    readers.forEach((h, k) => { if (h.fmt) jw.getColumn(k + 1).numFmt = h.fmt; });
    jw.getCell(`A${HDR}`).note = `Formulas only — nothing is typed on this tab. Paste the JC600 export on the ${SH.jc} tab at A1.`;

    // Cached results: what the formulas give for the lines loaded now.
    const jcRaw = jc ? jc.rows : [];
    const jcLines = jc ? jc.lines : [];
    readers.forEach((h, k) => {
        const col = colL(k + 1);
        const range = `${col}${R0}:${col}${JL}`;
        const result = (r) => {
            const rw = jcRaw[r - R0];
            const l = jcLines[r - R0];
            return rw && l ? h.v({ ...l, raw: rw }) : undefined;
        };
        if (h.array) jw.fillFormula(range, h.array, result, 'array');
        else jw.fillFormula(range, `IF($A${R0}="","",${h.f(R0)})`, result);
    });

    // Row 4: the payroll lines' totals.
    const jtot = (addr, formula, result, fmt) => {
        const c = jw.getCell(addr);
        c.value = fx(formula, result);
        c.numFmt = fmt;
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SUBTLE } };
    };
    jw.getCell('A4').value = 'Payroll lines only (Payroll? = TRUE) →';
    jw.getCell('A4').font = { bold: true, color: { argb: INK } };
    const payRange = `H${R0}:H${JL}`;
    jtot('C4', `SUMIFS(C${R0}:C${JL},${payRange},TRUE)`, round2(jt.amt), MONEY);
    jtot('D4', `SUMIFS(D${R0}:D${JL},${payRange},TRUE)`, round2(jt.hrs), HOURS);
    jtot('H4', `COUNTIF(${payRange},TRUE)`, jt.lines, '#,##0');
    jtot('L4', `MAX(L${R0}:L${JL})`, jt.latest ? xDate(jt.latest) : 0, 'm/d/yy;;"—"');
    jtot('N4', `SUMIFS(N${R0}:N${JL},${payRange},TRUE)`, round2(jt.baseAmt), MONEY);
    jw.autoFilter = { from: { row: HDR, column: 1 }, to: { row: HDR, column: readers.length } };

    // ── Summary, continued: the invoice off the tabs ──
    section(14, 'Invoice');
    const lines = [
        ['Labor hours', fx(`SUM(${LI('G')})`, m.totals.hrs), HOURS],
        ['    straight time hours', fx(`SUM(${LI('J')})`, m.totals.st), HOURS],
        ['    overtime hours', fx(`SUM(${LI('K')})`, m.totals.ot), HOURS],
        ['Labor', fx(`SUM(${LI('Q')})`, m.totals.labor), MONEY],
        ['Per diem days', fx(`SUM(${PD('E')})`, m.totals.pdDays), HOURS],
        ['Per diem', fx(`SUM(${PD('G')})`, m.totals.pd), MONEY],
        ['Equipment (rate sheet)', fx(`SUM(${EI('S')})`, m.totals.equip), MONEY],
        ['Fuel surcharge', fx(`SUM(${EI('V')})`, m.totals.fuel), MONEY],
        ['Equipment total', fx('B21+B22', m.totals.equipTotal), MONEY],
        ['LABOR + EQUIPMENT TOTAL', fx('B18+B20+B23', m.totals.total), MONEY],
    ];
    lines.forEach(([label, v, fmt], i) => {
        const r = 15 + i;
        s.getCell(`A${r}`).value = label;
        const c = s.getCell(`B${r}`);
        c.value = v; c.numFmt = fmt;
        if (/^(Labor|Per diem|Equipment total|LABOR)/.test(label) && fmt === MONEY) {
            s.getCell(`A${r}`).font = { bold: true };
            c.font = { bold: true };
        }
        if (label.startsWith('LABOR +')) {
            [s.getCell(`A${r}`), c].forEach((x) => {
                x.font = { bold: true, size: 12 };
                x.border = { top: { style: 'thin', color: { argb: GREY } }, bottom: { style: 'double', color: { argb: GREY } } };
            });
        }
    });

    let r0 = 27;
    section(r0, 'By week');
    const bwCols = [{ h: 'Week ending', fmt: DATE }, { h: 'Labor hours', fmt: HOURS, right: true }, { h: 'Labor', fmt: MONEY, right: true }, { h: 'Per diem', fmt: MONEY, right: true },
        { h: 'Equipment', fmt: MONEY, right: true }, { h: 'Fuel surcharge', fmt: MONEY, right: true }, { h: 'Total', fmt: MONEY, right: true }, { h: 'Labor from' }, { h: 'Equipment from' }];
    tableHeader(s, r0 + 1, bwCols);
    s.getColumn(1).width = 52;
    const basisWord = (b) => (b === 'card' ? 'Time card' : b === 'hj' ? 'HeavyJob' : '—');
    m.byWeek.forEach((w, i) => {
        const r = r0 + 2 + i;
        writeRow(s, r, bwCols, [xDate(w.we),
            fx(`SUMIFS(${LI('G')},${LI('A')},$A${r})`, w.hrs), fx(`SUMIFS(${LI('Q')},${LI('A')},$A${r})`, w.labor),
            fx(`SUMIFS(${PD('G')},${PD('A')},$A${r})`, w.pd), fx(`SUMIFS(${EI('S')},${EI('A')},$A${r})`, w.equip),
            fx(`SUMIFS(${EI('V')},${EI('A')},$A${r})`, w.fuel), fx(`C${r}+D${r}+E${r}+F${r}`, w.total), basisWord(w.laborBasis), basisWord(w.equipBasis)]);
        s.getCell(`A${r}`).alignment = { horizontal: 'left' };
    });
    const bwA = r0 + 2; const bwB = r0 + 1 + Math.max(m.byWeek.length, 1);
    totalRow(s, bwB + 1, bwCols, ['Total', fx(`SUM(B${bwA}:B${bwB})`, m.totals.hrs), fx(`SUM(C${bwA}:C${bwB})`, m.totals.labor), fx(`SUM(D${bwA}:D${bwB})`, m.totals.pd),
        fx(`SUM(E${bwA}:E${bwB})`, m.totals.equip), fx(`SUM(F${bwA}:F${bwB})`, m.totals.fuel), fx(`SUM(G${bwA}:G${bwB})`, m.totals.total), null, null]);

    r0 = bwB + 4;
    section(r0, 'Labor by classification');
    const bcCols = [{ h: 'Classification' }, { h: 'ST hours', fmt: HOURS, right: true }, { h: 'OT hours', fmt: HOURS, right: true }, { h: 'Hours', fmt: HOURS, right: true }, { h: 'Labor', fmt: MONEY, right: true }];
    tableHeader(s, r0 + 1, bcCols);
    m.byClass.forEach((c, i) => {
        const r = r0 + 2 + i;
        writeRow(s, r, bcCols, [c.cls || '(no classification)',
            fx(`SUMIFS(${LI('J')},${LI('D')},$A${r})`, c.st), fx(`SUMIFS(${LI('K')},${LI('D')},$A${r})`, c.ot),
            fx(`SUMIFS(${LI('G')},${LI('D')},$A${r})`, c.hrs), fx(`SUMIFS(${LI('Q')},${LI('D')},$A${r})`, c.amt)]);
    });
    const bcA = r0 + 2; const bcB = r0 + 1 + Math.max(m.byClass.length, 1);
    totalRow(s, bcB + 1, bcCols, ['Total', fx(`SUM(B${bcA}:B${bcB})`, m.totals.st), fx(`SUM(C${bcA}:C${bcB})`, m.totals.ot), fx(`SUM(D${bcA}:D${bcB})`, m.totals.hrs), fx(`SUM(E${bcA}:E${bcB})`, m.totals.labor)]);

    r0 = bcB + 4;
    section(r0, 'Equipment by rate sheet item');
    const biCols = [{ h: 'Rate sheet item' }, { h: 'UOM' }, { h: 'Rate', fmt: RATE, right: true }, { h: 'Billed qty', fmt: HOURS, right: true }, { h: 'Amount', fmt: MONEY, right: true }, { h: 'Fuel surcharge', fmt: MONEY, right: true }];
    tableHeader(s, r0 + 1, biCols);
    m.byItem.forEach((x, i) => {
        const r = r0 + 2 + i;
        writeRow(s, r, biCols, [x.item, x.uom, x.rate,
            fx(`SUMIFS(${EI('R')},${EI('E')},$A${r})`, x.qty), fx(`SUMIFS(${EI('S')},${EI('E')},$A${r})`, x.amt), fx(`SUMIFS(${EI('V')},${EI('E')},$A${r})`, x.fuel)]);
    });
    const biA = r0 + 2; const biB = r0 + 1 + Math.max(m.byItem.length, 1);
    totalRow(s, biB + 1, biCols, ['Total', null, null, null, fx(`SUM(E${biA}:E${biB})`, round2(m.byItem.reduce((a, x) => a + x.amt, 0))), fx(`SUM(F${biA}:F${biB})`, round2(m.byItem.reduce((a, x) => a + x.fuel, 0)))]);

    r0 = biB + 4;
    section(r0, 'Check before invoicing');
    // The two wage checks are formulas over the Labor Invoice's Wage note and
    // the JC600's latest week, so they follow a JC600 pasted in later; they
    // give the same words as unpricedCheck and carriedCheck.
    const count = (prefix) => `COUNTIF(${LI('T')},${str(`${prefix}*`)})`;
    const latest = `MAX(${J(JC.we)})`;
    const latestF = `IF(${latest}=0,${str(latestText(null))},"WE "&TEXT(${latest},"m/d/yy")&".")`;
    const people = (n) => `${n}&" person-week"&IF(${n}=1,"","s")`;
    const wageCheckF = {
        unpriced: (n = count('no JC600 wage')) => `"•  "&IF(${n}=0,${str(unpricedCheck(0))},${people(n)}&" with no JC600 wage, so not priced (see Wage note on Labor Invoice). Latest JC600 week: "&${latestF})`,
        carried: (n = count('no JC600 payroll this week')) => `"•  "&IF(${n}=0,${str(carriedCheck(0))},${people(n)}&" priced on the wage of another JC600 week (see Wage note on Labor Invoice): the JC600 has no payroll for them in the week billed. Latest JC600 week: "&${latestF})`,
    };
    [...m.checks, ...embedNotes.map((text) => ({ text }))].forEach((f, i) => {
        const c = s.getCell(`A${r0 + 1 + i}`);
        c.value = f.key ? fx(wageCheckF[f.key](), `•  ${f.text}`) : `•  ${f.text}`;
        c.alignment = { wrapText: true, vertical: 'top' };
        s.mergeCells(`A${r0 + 1 + i}:I${r0 + 1 + i}`);
        s.getRow(r0 + 1 + i).height = Math.max(16, Math.ceil(f.text.length / 150) * 15);
    });

    // Print: landscape, a page wide.
    Object.values(ws).forEach((w) => {
        if (!ours.has(w.name)) return; // a copied card keeps its own page setup
        w.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 } };
    });
    wb.embedNotes = embedNotes;
    wb.cardTabs = cards.map((c) => ({ we: c.we, tabs: Object.values(c.names) }));
    return wb;
};

/** Build the workbook and hand it to the browser; returns { name, notes, cardTabs }. */
export const downloadInvoiceWorkbook = async (model, opts = {}) => {
    const wb = await buildInvoiceWorkbook(model, opts);
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = workbookFileName(model);
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return { name, notes: wb.embedNotes, cardTabs: wb.cardTabs };
};
